import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import './App.css';
import FlightDetailPage from './pages/FlightDetailPage';
import { SyllabusListPage, SyllabusEditorPage } from './pages/SyllabusPage';
import { TrainingProgramCard, EnrollmentProgressPage } from './pages/Enrollment';
import { StudentsPage, CfiFlights } from './pages/Students';
import { DashboardPage } from './pages/Dashboard';
import { AircraftPage } from './pages/Aircraft';
import { DispatchPage } from './pages/Dispatch';
import { SchedulePage } from './pages/Schedule';
import { MaintForecastPanel } from './pages/MaintForecast';
import { BillingPage } from './pages/Billing';
import { DocumentTemplatesPage, PilotDocumentsCard, PilotDocumentsPage } from './pages/Documents';

// ── Auth Context ──
const AuthContext = createContext(null);
function AuthProvider({ children }) {
  const [user, setUser] = useState(() => { try { return JSON.parse(localStorage.getItem('school_user')); } catch { return null; } });
  const [token, setToken] = useState(() => localStorage.getItem('school_token'));

  const login = (u, t) => { setUser(u); setToken(t); localStorage.setItem('school_user', JSON.stringify(u)); localStorage.setItem('school_token', t); };
  const logout = () => { setUser(null); setToken(null); localStorage.removeItem('school_user'); localStorage.removeItem('school_token'); };
  const updateUser = (patch) => setUser(prev => { const u = { ...(prev || {}), ...patch }; localStorage.setItem('school_user', JSON.stringify(u)); return u; });
  // The stored user is a login-time snapshot; refresh it once per app load so
  // role/tab/branding changes reach people without a re-login.
  useEffect(() => { if (token) apiFetch('/api/auth/me', token).then(d => d.user && updateUser(d.user)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  return <AuthContext.Provider value={{ user, token, login, logout, updateUser }}>{children}</AuthContext.Provider>;
}
const useAuth = () => useContext(AuthContext);

async function apiFetch(url, token, opts = {}) {
  const hdrs = { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) };
  const res = await fetch(url, { ...opts, headers: hdrs });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `HTTP ${res.status}`); }
  return res.json();
}
async function apiPost(url, body, token) {
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `HTTP ${res.status}`); }
  return res.json();
}
async function apiDelete(url, token) {
  const res = await fetch(url, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `HTTP ${res.status}`); }
  return res.json();
}
async function apiPut(url, body, token) {
  const res = await fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `HTTP ${res.status}`); }
  return res.json();
}

// ── App Shell ──
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app-shell">
          <Header />
          <main className="main-content">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage apiFetch={apiFetch} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/fleet" element={<ProtectedRoute><FleetPage /></ProtectedRoute>} />
              <Route path="/fleet/:fleetId" element={<ProtectedRoute><AircraftPage apiFetch={apiFetch} apiPost={apiPost} apiPut={apiPut} apiDelete={apiDelete} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/dispatch" element={<ProtectedRoute><DispatchPage apiFetch={apiFetch} apiPost={apiPost} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/schedule" element={<ProtectedRoute><SchedulePage apiFetch={apiFetch} apiPost={apiPost} apiPut={apiPut} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/billing" element={<ProtectedRoute><BillingPage apiFetch={apiFetch} apiPost={apiPost} apiPut={apiPut} apiDelete={apiDelete} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/pilots" element={<ProtectedRoute><PilotsPage /></ProtectedRoute>} />
              <Route path="/pilots/:linkId" element={<ProtectedRoute><PilotDetailPage /></ProtectedRoute>} />
              <Route path="/documents" element={<ProtectedRoute><DocumentTemplatesPage apiFetch={apiFetch} apiPost={apiPost} apiPut={apiPut} apiDelete={apiDelete} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/documents/:linkId" element={<ProtectedRoute><PilotDocumentsPage apiFetch={apiFetch} apiPut={apiPut} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/scores/:scoreId" element={<ProtectedRoute><ScoreDetailPage /></ProtectedRoute>} />
              <Route path="/flights/:sid" element={<ProtectedRoute><FlightDetailPage apiFetch={apiFetch} apiPost={apiPost} apiPut={apiPut} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/students" element={<ProtectedRoute><StudentsPage apiFetch={apiFetch} apiPost={apiPost} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/syllabus" element={<ProtectedRoute><SyllabusListPage apiFetch={apiFetch} apiPost={apiPost} apiPut={apiPut} apiDelete={apiDelete} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/syllabus/:syllabusId" element={<ProtectedRoute><SyllabusEditorPage apiFetch={apiFetch} apiPost={apiPost} apiPut={apiPut} apiDelete={apiDelete} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/enrollments/:enrollmentId" element={<ProtectedRoute><EnrollmentProgressPage apiFetch={apiFetch} apiPost={apiPost} apiPut={apiPut} apiDelete={apiDelete} useAuth={useAuth} /></ProtectedRoute>} />
              <Route path="/frat" element={<ProtectedRoute><FratPage /></ProtectedRoute>} />
              <Route path="/cfis" element={<ProtectedRoute><CFIsPage /></ProtectedRoute>} />
              <Route path="/training" element={<ProtectedRoute><TrainingCoursesPage /></ProtectedRoute>} />
              <Route path="/acs" element={<ProtectedRoute><ACSVerificationsPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/activate" element={<ActivatePage />} />
              <Route path="/" element={<RootRedirect />} />
            </Routes>
          </main>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

function RootRedirect() {
  const { user } = useAuth();
  return user ? <Navigate to="/dashboard" /> : <Navigate to="/login" />;
}

// Maintenance staff only have the Fleet area (server enforces the same in core.ROLE_ROUTE_ALLOWLIST).
const FLEET_ONLY_ROLES = ['maintenance'];
function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" />;
  if (FLEET_ONLY_ROLES.includes(user.role) && !location.pathname.startsWith('/fleet')) return <Navigate to="/fleet" replace />;
  return children;
}

// ── Header ──
function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) return null;

  // Per-user tab visibility: user.tabs (set on the Settings staff table) wins;
  // otherwise the role default below. Settings always follows role, never a checkbox.
  const ROLE_DEFAULT_TABS = {
    cfi: ['dashboard', 'dispatch', 'schedule', 'pilots', 'students', 'frat', 'cfis', 'training', 'acs', 'syllabus'],
    dispatcher: ['dashboard', 'fleet', 'dispatch', 'schedule', 'billing', 'pilots'],
    maintenance: ['fleet'],
  }; // owner/admin default: all tabs
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/fleet', label: 'Fleet', icon: '✈️' },
    { path: '/dispatch', label: 'Dispatch', icon: '🛫' },
    { path: '/schedule', label: 'Schedule', icon: '🗓️' },
    { path: '/billing', label: 'Billing', icon: '💵' },
    { path: '/pilots', label: 'Pilots', icon: '👨‍✈️' },
    { path: '/students', label: 'Students', icon: '🎒' },
    { path: '/frat', label: 'FRAT', icon: '📋' },
    { path: '/cfis', label: 'CFIs', icon: '🎓' },
    { path: '/training', label: 'Training', icon: '📚' },
    { path: '/acs', label: 'ACS Log', icon: '✅' },
    { path: '/syllabus', label: 'Syllabus', icon: '📘' },
    { path: '/settings', label: 'Settings', icon: '⚙️' },
  ];
  const allowedTabs = (FLEET_ONLY_ROLES.includes(user.role) ? null : user.tabs) || ROLE_DEFAULT_TABS[user.role] || null;
  const visibleNav = navItems.filter(it => it.path === '/settings'
    ? (user.role === 'owner' || user.role === 'admin')
    : (!allowedTabs || allowedTabs.includes(it.path.slice(1))));

  return (
    <header className="header">
      <div className="header-top">
        <div className="header-brand" onClick={() => navigate(FLEET_ONLY_ROLES.includes(user.role) ? '/fleet' : '/dashboard')}>
          {user.school_logo ? <img src={user.school_logo} alt="" style={{ height: 36, maxWidth: 120, objectFit: 'contain', borderRadius: 6 }} /> : <span className="brand-icon">🏫</span>}
          <div>
            <div className="brand-name">{user.school_name || 'School Portal'}</div>
            <div className="brand-sub">{user.icao_base || 'Flight School Management'}</div>
          </div>
        </div>
        <div className="header-user">
          <span className="user-name">{user.display_name}</span>
          <span className="user-role">{user.role}</span>
          <button className="btn-ghost" onClick={() => { logout(); navigate('/login'); }}>Sign Out</button>
        </div>
      </div>
      <nav className="header-nav">
        {visibleNav.map(item => (
          <button key={item.path}
            className={`nav-tab ${location.pathname === item.path ? 'active' : ''}`}
            onClick={() => navigate(item.path)}>
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </header>
  );
}

// ── Login ──

// ── Activate (console-created school admin sets password) ──
function ActivatePage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token') || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!token) return (
    <div className="auth-page"><div className="auth-card">
      <div className="auth-header"><span style={{ fontSize:40 }}>⚠️</span><h1>Invalid Link</h1><p>No activation token found. Please use the link from your welcome email.</p></div>
    </div></div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const data = await apiPost('/api/auth/activate', { token, password });
      // Fetch user profile with the new session token
      const me = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${data.token}` } }).then(r => r.json());
      login(me, data.token);
      navigate('/dashboard');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <span style={{ fontSize:40 }}>🏫</span>
          <h1>Set Your Password</h1>
          <p>Welcome to BMAC3 School Portal. Create a password to get started.</p>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label>New Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" required />
          </div>
          <div className="form-group">
            <label>Confirm Password</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" required />
          </div>
          <button type="submit" className="btn-primary btn-full" disabled={loading}>
            {loading ? 'Setting up…' : 'Set Password & Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const data = await apiPost('/api/auth/login', { email, password });
      login(data.user, data.token);
      navigate('/dashboard');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <span style={{ fontSize: 40 }}>🏫</span>
          <h1>School Portal</h1>
          <p>Flight School Management Platform</p>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label>Email or username</label>
            <input type="text" autoCapitalize="none" autoCorrect="off" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@flightschool.com" required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••" required />
          </div>
          <button type="submit" className="btn-primary btn-full" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <div className="auth-footer">
          Don't have an account? <Link to="/register">Register your school</Link>
        </div>
      </div>
    </div>
  );
}

// ── Register ──
function RegisterPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ school_name: '', email: '', password: '', display_name: '', icao_base: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const data = await apiPost('/api/auth/register', form);
      login(data.user, data.token);
      navigate('/dashboard');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <span style={{ fontSize: 40 }}>🏫</span>
          <h1>Register Your School</h1>
          <p>Set up your flight school management portal</p>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label>School Name *</label>
            <input value={form.school_name} onChange={e => set('school_name', e.target.value)} placeholder="East Coast Aviation" required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Home Airport</label>
              <input value={form.icao_base} onChange={e => set('icao_base', e.target.value.toUpperCase())} placeholder="KBDR" maxLength={4} />
            </div>
            <div className="form-group">
              <label>Your Name *</label>
              <input value={form.display_name} onChange={e => set('display_name', e.target.value)} placeholder="John Smith" required />
            </div>
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="admin@flightschool.com" required />
          </div>
          <div className="form-group">
            <label>Password *</label>
            <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="At least 6 characters" required minLength={6} />
          </div>
          <button type="submit" className="btn-primary btn-full" disabled={loading}>
            {loading ? 'Creating...' : 'Create School Account'}
          </button>
        </form>
        <div className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

// Maintenance + squawk preview on a fleet card. Clicking a line lands on the
// matching tab of the aircraft Set up page (?tab= deep link).
function FleetMaintPreview({ m, go }) {
  if (!m) return null;
  const dueTxt = i => {
    const p = [];
    if (i.remaining_hours != null) p.push(i.remaining_hours < 0 ? `${Math.abs(i.remaining_hours).toFixed(1)} h over` : `in ${Number(i.remaining_hours).toFixed(1)} h`);
    if (i.remaining_days != null) p.push(i.remaining_days < 0 ? `${-i.remaining_days} d over` : `in ${i.remaining_days} d`);
    return p.join(' · ');
  };
  const urgent = m.items.filter(i => i.status === 'overdue' || i.status === 'due_soon')
    .sort((x, y) => (x.status === 'overdue' ? 0 : 1) - (y.status === 'overdue' ? 0 : 1));
  const grounding = m.squawks.filter(q => q.severity === 'grounding' && q.status === 'open').length;
  const others = m.squawks.length - grounding;
  const noBaseline = m.items.filter(i => i.status === 'unknown').length;
  const nextOk = m.items.filter(i => i.status === 'ok')
    .map(i => ({ i, k: Math.min(i.remaining_days != null ? i.remaining_days : 1e9, i.remaining_hours != null ? i.remaining_hours * 10 : 1e9) }))
    .sort((x, y) => x.k - y.k).map(x => x.i)[0];
  return <div style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0', fontSize: 12 }}>
    {m.items.length === 0 ? (
      <div onClick={() => go('maint')} style={{ color: 'var(--text-muted)', cursor: 'pointer' }}>No maintenance schedule — set one up →</div>
    ) : (
      <div onClick={() => go('maint')} style={{ cursor: 'pointer' }}>
        {urgent.slice(0, 2).map(i => <div key={i.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 8, background: i.status === 'overdue' ? '#fee2e2' : '#fef3c7', color: i.status === 'overdue' ? '#991b1b' : '#92400e' }}>{i.status === 'overdue' ? 'OVERDUE' : 'DUE SOON'}</span>
          <span>{i.name}</span><span className="mono" style={{ color: 'var(--text-muted)', fontSize: 11 }}>{dueTxt(i)}</span>
        </div>)}
        {urgent.length > 2 && <div style={{ color: 'var(--text-muted)' }}>+{urgent.length - 2} more due →</div>}
        {urgent.length === 0 && (nextOk
          ? <div style={{ color: '#065f46' }}>✓ Maintenance on schedule <span style={{ color: 'var(--text-muted)' }}>· next: {nextOk.name} {dueTxt(nextOk)}</span></div>
          : <div style={{ color: '#92400e' }}>{noBaseline} item{noBaseline === 1 ? '' : 's'} with no last-done recorded — set baseline →</div>)}
      </div>
    )}
    {(grounding > 0 || others > 0) && <div onClick={() => go('squawks')} style={{ cursor: 'pointer', marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {grounding > 0 && <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 7px', borderRadius: 8, background: '#fee2e2', color: '#991b1b' }}>⛔ {grounding} grounding squawk{grounding > 1 ? 's' : ''}</span>}
      {others > 0 && <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 7px', borderRadius: 8, background: '#fef3c7', color: '#92400e' }}>⚠ {others} open squawk{others > 1 ? 's' : ''}</span>}
    </div>}
  </div>;
}

function FleetPage() {
  const { token, user } = useAuth();
  const canManageFleet = ['owner', 'admin'].includes(user?.role);   // add/remove aircraft
  const navigate = useNavigate();
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newTail, setNewTail] = useState('');
  const [newType, setNewType] = useState('');
  const [newNick, setNewNick] = useState('');
  const [error, setError] = useState('');
  const [lookupInfo, setLookupInfo] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [acTypes, setAcTypes] = useState([]);
  const [maintByFleet, setMaintByFleet] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editType, setEditType] = useState('');
  const [editNick, setEditNick] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const lookupTimer = React.useRef(null);

  const loadFleet = () => {
    apiFetch('/api/fleet', token).then(d => setFleet(d.fleet || [])).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => {
    loadFleet();
    apiFetch('/api/fleet/ac-types', token).then(d => setAcTypes(d.ac_types || [])).catch(() => {});
    apiFetch('/api/dashboard/maintenance', token).then(d => {
      const m = {}; (d.aircraft || []).forEach(x => { m[x.fleet_id] = x; }); setMaintByFleet(m);
    }).catch(() => {});
  }, [token]);

  // Auto-lookup tail number
  const handleTailChange = (val) => {
    val = val.toUpperCase();
    setNewTail(val);
    setLookupInfo(null);
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (val.length >= 4) {
      lookupTimer.current = setTimeout(async () => {
        setLookupLoading(true);
        try {
          const d = await apiFetch(`/api/fleet/lookup-tail?tail=${encodeURIComponent(val)}`, token);
          setLookupInfo(d);
          if (d.found && d.ac_type && !newType) setNewType(d.ac_type);
        } catch { }
        setLookupLoading(false);
      }, 400);
    }
  };

  const addAircraft = async (e) => {
    e.preventDefault(); setError('');
    try {
      await apiPost('/api/fleet', { tail_number: newTail, ac_type: newType, nickname: newNick }, token);
      setNewTail(''); setNewType(''); setNewNick(''); setAdding(false); setLookupInfo(null);
      loadFleet();
    } catch (err) { setError(err.message); }
  };

  const removeAircraft = async (id, tail) => {
    if (!window.confirm(`Remove ${tail} from fleet?`)) return;
    try { await apiDelete(`/api/fleet/${id}`, token); loadFleet(); }
    catch (err) { alert(err.message); }
  };

  const startEdit = (a) => {
    setEditingId(a.id); setEditType(a.ac_type || ''); setEditNick(a.nickname || ''); setEditStatus(a.status || 'active');
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (id) => {
    try {
      await apiPut(`/api/fleet/${id}`, { ac_type: editType, nickname: editNick, status: editStatus }, token);
      setEditingId(null);
      loadFleet();
    } catch (err) { alert(err.message); }
  };

  if (loading) return <div className="loading-state">Loading fleet...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Fleet Management</h1>
        {canManageFleet && <button className="btn-primary" onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : '+ Add Aircraft'}</button>}
      </div>

      {adding && (
        <div className="card">
          <h3>Add Aircraft to Fleet</h3>
          <form onSubmit={addAircraft}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-row">
              <div className="form-group">
                <label>Tail Number *</label>
                <input value={newTail} onChange={e => handleTailChange(e.target.value)} placeholder="N223MR" required />
                {lookupLoading && <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>Looking up...</div>}
                {lookupInfo && lookupInfo.found && (
                  <div style={{ fontSize: 11, color: '#10b981', marginTop: 4 }}>
                    ✓ {lookupInfo.manufacturer} {lookupInfo.model}
                    {lookupInfo.ac_type && <span style={{ fontWeight: 700 }}> → {lookupInfo.ac_type}</span>}
                  </div>
                )}
                {lookupInfo && !lookupInfo.found && (
                  <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>⚠ Not found in FAA registry</div>
                )}
              </div>
              <div className="form-group">
                <label>Type</label>
                <div style={{ position: 'relative' }}>
                  <input value={newType} onChange={e => setNewType(e.target.value.toUpperCase())}
                    placeholder="Auto-detected or select" list="ac-type-list" />
                  <datalist id="ac-type-list">
                    {acTypes.map(t => <option key={t} value={t} />)}
                  </datalist>
                </div>
              </div>
              <div className="form-group">
                <label>Nickname</label>
                <input value={newNick} onChange={e => setNewNick(e.target.value)} placeholder="Charlie Romeo" />
              </div>
            </div>
            <button type="submit" className="btn-primary">Add to Fleet</button>
          </form>
        </div>
      )}

      {fleet.length === 0 ? (
        <div className="empty-state">
          <span style={{ fontSize: 48 }}>✈️</span>
          <h3>No Aircraft in Fleet</h3>
          <p>Add N-numbers to start tracking your fleet's approach scores.</p>
        </div>
      ) : (<>
        <MaintForecastPanel apiFetch={apiFetch} token={token} navigate={navigate} role={user?.role} />
        <div className="card-grid">
          {fleet.map(a => (
            <div key={a.id} className="card aircraft-card">
              {editingId === a.id ? (
                <>
                  <div className="aircraft-header">
                    <div>
                      <div className="aircraft-tail">{a.tail_number}</div>
                    </div>
                  </div>
                  <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Type</label>
                      <input value={editType} onChange={e => setEditType(e.target.value.toUpperCase())}
                        list="ac-type-edit-list" placeholder="Select type" style={{ width: '100%', boxSizing: 'border-box' }} />
                      <datalist id="ac-type-edit-list">
                        {acTypes.map(t => <option key={t} value={t} />)}
                      </datalist>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Nickname</label>
                      <input value={editNick} onChange={e => setEditNick(e.target.value)} placeholder="Optional" style={{ width: '100%', boxSizing: 'border-box' }} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Status</label>
                      <select value={editStatus} onChange={e => setEditStatus(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                        <option value="active">Active</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="retired">Retired</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-primary" style={{ flex: 1 }} onClick={() => saveEdit(a.id)}>Save</button>
                    <button className="btn-ghost" style={{ flex: 1 }} onClick={cancelEdit}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="aircraft-header">
                    <div>
                      <div className="aircraft-tail">{a.tail_number}</div>
                      <div className="aircraft-type">{a.ac_type || 'Unknown Type'}{a.nickname ? ` · ${a.nickname}` : ''}</div>
                    </div>
                    <span className={`status-badge ${a.status}`}>{a.status}</span>
                  </div>
                  <div className="aircraft-stats">
                    <div className="aircraft-stat">
                      <div className="stat-num">{a.total_approaches || 0}</div>
                      <div className="stat-lbl">Approaches</div>
                    </div>
                    <div className="aircraft-stat">
                      <div className="stat-num">{a.avg_score ? `${a.avg_score}%` : '—'}</div>
                      <div className="stat-lbl">Avg Score</div>
                    </div>
                    <div className="aircraft-stat">
                      <div className="stat-num">{a.last_flight || '—'}</div>
                      <div className="stat-lbl">Last Flight</div>
                    </div>
                  </div>
                  <FleetMaintPreview m={maintByFleet[a.id]} go={t => navigate(`/fleet/${a.id}?tab=${t}`)} />
                  <div className="aircraft-actions" style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-primary" onClick={() => navigate(`/fleet/${a.id}`)}>Set up</button>
                    <button className="btn-ghost" onClick={() => startEdit(a)}>Edit</button>
                    {canManageFleet && <button className="btn-ghost btn-danger" onClick={() => removeAircraft(a.id, a.tail_number)}>Remove</button>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </>)}
    </div>
  );
}

// ── Pilots (placeholder) ──
// ── Pilots Page ──
function PilotsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [pilots, setPilots] = useState([]);
  const [nonAffiliated, setNonAffiliated] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addUser, setAddUser] = useState(null);
  const [loginFor, setLoginFor] = useState(null); const [loginState, setLoginState] = useState(null);
  const [ownerFor, setOwnerFor] = useState(null);
  const [loginEmail, setLoginEmail] = useState(''); const [loginBusy, setLoginBusy] = useState(false);
  const canManageLogin = ['owner', 'admin'].includes(user?.role);
  const openLogin = async (p) => { setLoginFor(p); setLoginState(null); setLoginEmail(p.pilot_username || ''); try { setLoginState(await apiFetch(`/api/pilots/${p.id}/login`, token)); } catch (e) { alert(e.message); setLoginFor(null); } };
  const saveLogin = async (send) => { setLoginBusy(true); try { const r = await apiPut(`/api/pilots/${loginFor.id}/login`, { email: loginEmail, send_link: send }, token);
    alert(r.warning ? r.warning : send ? `Link sent to ${r.sent_to}` : r.email_changed ? `Login changed to ${r.email}` : 'No change'); setLoginFor(null); load(); } catch (e) { alert(e.message); } setLoginBusy(false); };
  const [addMsg, setAddMsg] = useState(null);

  const load = () => {
    apiFetch('/api/pilots', token).then(d => {
      setPilots(d.pilots || []); setNonAffiliated(d.non_affiliated || []); setTiers(d.tiers || []);
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, [token]);

  const pending = pilots.filter(p => p.status === 'pending');
  const active = pilots.filter(p => p.status === 'active');

  const acceptPilot = async (linkId, tierId) => {
    try { await apiPost(`/api/pilots/${linkId}/accept`, { tier_id: tierId }, token); load(); }
    catch (err) { alert(err.message); }
  };
  const rejectPilot = async (linkId) => {
    if (!window.confirm('Reject this pilot request?')) return;
    try { await apiPost(`/api/pilots/${linkId}/reject`, {}, token); load(); }
    catch (err) { alert(err.message); }
  };
  const changeTier = async (linkId, tierId) => {
    try { await apiPost(`/api/pilots/${linkId}/tier`, { tier_id: tierId }, token); load(); }
    catch (err) { alert(err.message); }
  };
  const unlinkPilot = async (linkId, name) => {
    if (!window.confirm(`Unlink ${name || 'this pilot'} from your school?`)) return;
    try { await apiPost(`/api/pilots/${linkId}/unlink`, {}, token); load(); }
    catch (err) { alert(err.message); }
  };

  if (loading) return <div className="loading-state">Loading pilots...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div><h1>Pilots</h1><p>{active.length} linked · {pending.length} pending · {nonAffiliated.length} non-affiliated · <span style={{ color: '#2563eb', cursor: 'pointer' }} onClick={() => navigate('/documents')}>🪪 document templates</span></p></div>
        <button className="btn btn-primary" onClick={() => setAddUser({ display_name: '', email: '', role: 'student', tier_id: '' })}>+ Add user</button>
      {ownerFor && <OwnerAircraftModal pilot={ownerFor} token={token} onClose={() => setOwnerFor(null)} />}
      {loginFor && (
        <div className="modal-overlay" onClick={() => setLoginFor(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <h3 style={{ marginTop: 0 }}>App login — {loginFor.pilot_name || loginFor.pilot_username}</h3>
            {loginState === null ? <div className="muted">Loading…</div> : <>
              <div style={{ fontSize: 13, color: '#556', marginBottom: 12 }}>
                {loginState.never_set_password
                  ? <>⚠️ Has <b>never set a password</b>.{loginState.pending_link_expires ? <> A set-password link is pending (expires {new Date(loginState.pending_link_expires).toLocaleString()}).</> : ' No valid link outstanding.'}</>
                  : <>Password set. Last sign-in: {loginState.last_login ? new Date(loginState.last_login).toLocaleString() : 'never'}.</>}
              </div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Login email</label>
              <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} style={{ width: '100%', padding: 8, marginBottom: 12, borderRadius: 6, border: '1px solid var(--border)' }} />
              <div style={{ fontSize: 12, color: '#999', marginBottom: 12 }}>Changing the email changes what they sign in with on PROP 1 and the scores app. "Send link" emails a fresh 72-hour set-password link to this address and cancels any older link.</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn-ghost btn-sm" onClick={() => setLoginFor(null)}>Close</button>
                <button className="btn btn-sm" disabled={loginBusy} onClick={() => saveLogin(false)}>Save email</button>
                <button className="btn btn-primary btn-sm" disabled={loginBusy} onClick={() => saveLogin(true)}>Save &amp; send link</button>
              </div>
            </>}
          </div>
        </div>
      )}
      </div>
      {addUser && (
        <div onClick={() => setAddUser(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div onClick={e => e.stopPropagation()} className="card" style={{ width: 420, maxWidth: '94vw', padding: 18 }}>
            <h3 style={{ marginTop: 0 }}>Add a user</h3>
            <p style={{ fontSize: 12, color: '#667', marginTop: 0 }}>Creates their app login and links them to your school — they get an email to set a password. No app-side signup needed.</p>
            <div style={{ display: 'grid', gap: 8 }}>
              <input placeholder="Full name" value={addUser.display_name} onChange={e => setAddUser(u => ({ ...u, display_name: e.target.value }))} />
              <input placeholder="email@example.com" value={addUser.email} onChange={e => setAddUser(u => ({ ...u, email: e.target.value }))} />
              <select value={addUser.role} onChange={e => setAddUser(u => ({ ...u, role: e.target.value }))}>
                <option value="student">Student</option><option value="renter">Renter</option>
                <option value="pilot">Pilot</option><option value="cfi">Instructor (CFI)</option>
              </select>
              <select value={addUser.tier_id} onChange={e => setAddUser(u => ({ ...u, tier_id: e.target.value }))}>
                <option value="">Tier (optional)</option>
                {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {addMsg?.err && <p style={{ color: '#dc2626', fontSize: 13, margin: 0 }}>{addMsg.err}</p>}
              {addMsg?.ok && <p style={{ color: '#16a34a', fontSize: 13, margin: 0 }}>{addMsg.ok}</p>}
              {addMsg?.warn && <p style={{ color: '#d97706', fontSize: 12, margin: 0 }}>{addMsg.warn}{addMsg.url && <> — share this link: <code style={{ fontSize: 10 }}>{addMsg.url}</code></>}</p>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn" onClick={() => { setAddUser(null); setAddMsg(null); }}>Close</button>
                <button className="btn btn-primary" onClick={async () => {
                  setAddMsg(null);
                  try {
                    const r = await apiPost('/api/pilots/create-user', { ...addUser, tier_id: addUser.tier_id ? Number(addUser.tier_id) : null }, token);
                    setAddMsg(r.warning ? { warn: r.warning, url: r.reset_url } : { ok: `${r.new_account ? 'Account created — invite' : 'Existing account linked — note'} sent to ${r.sent_to}` });
                    load();
                  } catch (e) { setAddMsg({ err: e.message }); }
                }}>Create &amp; invite</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── New Requests ── */}
      {pending.length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: '#f59e0b', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800 }}>{pending.length}</span>
            New Requests
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {pending.map(p => (
              <div key={p.id} className="pilot-request-card">
                <div className="pilot-info">
                  <div className="pilot-name">{p.pilot_name || p.pilot_username || 'Unknown Pilot'}</div>
                  <div className="pilot-meta">Requested {p.invited_at ? new Date(p.invited_at).toLocaleDateString() : ''}</div>
                </div>
                <div className="pilot-actions">
                  <select defaultValue="" onChange={e => { if (e.target.value) acceptPilot(p.id, parseInt(e.target.value)); }}
                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, fontWeight: 600 }}>
                    <option value="" disabled>Accept as...</option>
                    {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <button className="btn-ghost btn-danger btn-sm" onClick={() => rejectPilot(p.id)}>Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Flight School Pilots ── */}
      <div className="card">
        <h3>✈️ Flight School Pilots</h3>
        {active.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No linked pilots yet. Pilots can request to link from the BMAC3 app.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Pilot</th><th>Tier</th><th>Docs</th><th>Linked</th><th>Change Tier</th><th></th></tr>
              </thead>
              <tbody>
                {active.map(p => (
                  <tr key={p.id} onClick={() => navigate(`/pilots/${p.id}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="bold">{p.pilot_name || p.pilot_username || '—'}</div>
                      {p.pilot_username && <div style={{ fontSize: 11, color: '#999' }}>@{p.pilot_username}</div>}
                    </td>
                    <td><span className="tier-badge">{p.tier_name || 'Unassigned'}</span></td>
                    <td><DocsPill d={p.docs} /></td>
                    <td style={{ fontSize: 12, color: '#999' }}>{p.linked_at ? new Date(p.linked_at).toLocaleDateString() : '—'}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <select value={p.tier_id || ''} onChange={e => changeTier(p.id, parseInt(e.target.value))}
                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12 }}>
                        <option value="" disabled>Select</option>
                        {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      {canManageLogin && <button className="btn-ghost btn-sm" onClick={() => openLogin(p)} style={{ fontSize: 11, padding: '3px 8px', marginRight: 4 }}>Login</button>}
                      {canManageLogin && <button className="btn-ghost btn-sm" onClick={() => setOwnerFor(p)} style={{ fontSize: 11, padding: '3px 8px', marginRight: 4 }}>Aircraft</button>}
                      <button className="btn-ghost btn-danger btn-sm" onClick={() => unlinkPilot(p.id, p.pilot_name || p.pilot_username)}
                        style={{ fontSize: 11, padding: '3px 8px' }}>Unlink</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Non-Affiliated ── */}
      {nonAffiliated.length > 0 && (
        <div className="card">
          <h3>👤 Non-Affiliated</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            These pilots have claimed fleet aircraft in the app but haven't linked to your school.
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Pilot</th><th>Aircraft</th><th>Approaches</th><th>Avg Score</th></tr>
              </thead>
              <tbody>
                {nonAffiliated.map((p, i) => (
                  <tr key={i}>
                    <td className="bold">{p.pilot_name || p.pilot_username || '—'}</td>
                    <td className="mono">{p.tail_number}</td>
                    <td className="mono">{p.approach_count}</td>
                    <td className="mono">{p.avg_score ? `${p.avg_score}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── FRAT Builder ──
function FratPage() {
  const { token } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [categories, setCategories] = useState({});
  const [templates, setTemplates] = useState({});
  const [selectedTier, setSelectedTier] = useState(null);
  const [tierRules, setTierRules] = useState([]);
  const [tierDetail, setTierDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [addingTier, setAddingTier] = useState(false);
  const [newTierName, setNewTierName] = useState('');
  const [newTierDesc, setNewTierDesc] = useState('');
  const [presets, setPresets] = useState([]);
  const [showPresets, setShowPresets] = useState(false);

  const loadTiers = () => {
    apiFetch('/api/frat/tiers', token).then(d => setTiers(d.tiers || [])).catch(console.error);
  };
  const loadCats = () => {
    apiFetch('/api/frat/categories', token).then(d => setCategories(d.categories || {})).catch(console.error);
    apiFetch('/api/frat/templates', token).then(d => setTemplates(d.templates || {})).catch(console.error);
    apiFetch('/api/frat/presets', token).then(d => setPresets(d.presets || [])).catch(console.error);
  };

  useEffect(() => { loadTiers(); loadCats(); setLoading(false); }, [token]);

  const selectTier = async (tierId) => {
    setSelectedTier(tierId); setDirty(false);
    const d = await apiFetch(`/api/frat/${tierId}`, token);
    setTierRules(d.rules || []);
    setTierDetail(d.tier || null);
  };

  const applyTemplate = async (key) => {
    if (!window.confirm(`Apply the "${templates[key]?.name}" template? This will replace ALL existing tiers and rules.`)) return;
    try {
      await apiPost('/api/frat/apply-template', { template: key }, token);
      setShowTemplates(false); setSelectedTier(null); setTierRules([]);
      loadTiers();
    } catch (err) { alert(err.message); }
  };

  const addRule = (catKey) => {
    const cat = categories[catKey];
    if (!cat.multi && tierRules.find(r => r.category === catKey)) return; // already exists
    const textual = cat.type === 'text' || cat.type === 'list';
    setTierRules(prev => [...prev, {
      category: catKey, value_num: textual ? null : cat.type === 'bool' ? 0 : 10, value_text: textual ? '' : null,
      action: cat.type === 'text' ? 'caution' : 'block'
    }]);
    setDirty(true);
  };

  const updateRule = (idx, field, value) => {
    setTierRules(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    setDirty(true);
  };

  const removeRule = (idx) => {
    setTierRules(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      await apiPost(`/api/frat/${selectedTier}/rules`, { rules: tierRules }, token);
      setDirty(false); loadTiers();
    } catch (err) { alert(err.message); }
    setSaving(false);
  };

  const createTier = async (e) => {
    e.preventDefault();
    try {
      await apiPost('/api/frat/tiers', { name: newTierName, description: newTierDesc }, token);
      setNewTierName(''); setNewTierDesc(''); setAddingTier(false); loadTiers();
    } catch (err) { alert(err.message); }
  };

  const deleteTier = async (tierId, name) => {
    if (!window.confirm(`Delete tier "${name}" and all its rules?`)) return;
    try { await apiDelete(`/api/frat/tiers/${tierId}`, token); if (selectedTier === tierId) { setSelectedTier(null); setTierRules([]); } loadTiers(); }
    catch (err) { alert(err.message); }
  };

  const importPreset = async (presetKey) => {
    try {
      await apiPost(`/api/frat/${selectedTier}/import-preset`, { preset: presetKey }, token);
      setShowPresets(false);
      selectTier(selectedTier); // reload rules
      loadTiers();
    } catch (err) { alert(err.message); }
  };

  const actionColors = { block: '#ef4444', caution: '#f59e0b', require_cfi: '#8b5cf6', require_checkout: '#3b82f6' };
  const actionLabels = { block: '🚫 Block', caution: '⚠️ Caution', require_cfi: '👨‍✈️ Req CFI', require_checkout: '✅ Req Checkout' };
  const groupLabels = { weather: '🌦️ Weather Limits', currency: '📊 Currency Requirements', runway: '🛬 Runway / Airport', operations: '🕑 Operations / Desk Checks' };

  if (loading) return <div className="loading-state">Loading FRAT builder...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>FRAT Builder</h1>
          <p>Skills-based Flight Risk Assessment — every rule is digitally verifiable</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={() => setShowTemplates(!showTemplates)}>📋 Templates</button>
          <button className="btn-ghost" onClick={() => setAddingTier(!addingTier)}>+ Add Tier</button>
        </div>
      </div>

      {/* Template Picker */}
      {showTemplates && (
        <div className="card" style={{ borderColor: '#3b82f6', borderWidth: 2 }}>
          <h3>Apply FRAT Template</h3>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>Choose a starting template. You can customize all rules after applying.</p>
          <div className="template-grid">
            {Object.entries(templates).map(([key, t]) => (
              <div key={key} className="template-card" onClick={() => applyTemplate(key)}>
                <div className="template-name">{t.name}</div>
                <div className="template-desc">{t.description}</div>
                <div className="template-tiers">{t.tier_names.join(' → ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Tier Form */}
      {addingTier && (
        <div className="card">
          <h3>Add Pilot Tier</h3>
          <form onSubmit={createTier}>
            <div className="form-row">
              <div className="form-group"><label>Tier Name</label>
                <input value={newTierName} onChange={e => setNewTierName(e.target.value)} placeholder="e.g. Sport Pilot" required /></div>
              <div className="form-group"><label>Description</label>
                <input value={newTierDesc} onChange={e => setNewTierDesc(e.target.value)} placeholder="Who this tier applies to" /></div>
            </div>
            <button type="submit" className="btn-primary">Create Tier</button>
          </form>
        </div>
      )}

      <div className="frat-layout">
        {/* Tier List */}
        <div className="tier-list">
          {tiers.map(t => (
            <div key={t.id} className={`tier-card ${selectedTier === t.id ? 'active' : ''}`} onClick={() => selectTier(t.id)}>
              <div className="tier-header">
                <div className="tier-name">{t.name}</div>
                <span className="tier-meta">{t.rule_count || 0} rules · {t.pilot_count || 0} pilots</span>
              </div>
              <div className="tier-desc">{t.description}</div>
              <button className="btn-ghost btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); deleteTier(t.id, t.name); }}
                style={{ marginTop: 6, fontSize: 11 }}>Delete</button>
            </div>
          ))}
          {tiers.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No tiers yet. Apply a template or add one.</div>}
        </div>

        {/* Rule Editor */}
        <div className="rule-editor">
          {selectedTier ? (
            <>
              <div className="rule-editor-header">
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 800 }}>{tierDetail?.name} FRAT</h2>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{tierDetail?.description}</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-ghost" onClick={() => setShowPresets(!showPresets)}>📥 Import Preset</button>
                  <button className="btn-primary" disabled={!dirty || saving} onClick={saveRules}>
                    {saving ? 'Saving...' : dirty ? '💾 Save Rules' : 'Saved'}
                  </button>
                </div>
              </div>

              {/* Preset Picker */}
              {showPresets && (
                <div className="card" style={{ marginBottom: 12, borderColor: '#8b5cf6', borderWidth: 2 }}>
                  <h3>Import Rules from Template</h3>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Choose a preset to import its rules into <strong>{tierDetail?.name}</strong>. This replaces all current rules.</p>
                  <div className="preset-grid">
                    {presets.map(p => (
                      <div key={p.key} className="preset-card" onClick={() => {
                        if (window.confirm(`Import ${p.rule_count} rules from "${p.template} → ${p.tier}" into ${tierDetail?.name}?`))
                          importPreset(p.key);
                      }}>
                        <div className="preset-source">{p.template}</div>
                        <div className="preset-tier">{p.tier}</div>
                        <div className="preset-desc">{p.description}</div>
                        <div className="preset-count">{p.rule_count} rules</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Rules */}
              {tierRules.length > 0 && (
                <div className="rules-table">
                  {Object.entries(groupLabels).map(([group, label]) => {
                    const groupRules = tierRules.map((r, i) => ({ ...r, _idx: i })).filter(r => (categories[r.category] || {}).group === group);
                    if (groupRules.length === 0) return null;
                    return (
                      <div key={group} className="rule-group">
                        <div className="rule-group-label">{label}</div>
                        {groupRules.map(r => {
                          const cat = categories[r.category] || {};
                          return (
                            <div key={r._idx} className="rule-row">
                              <div className="rule-label">{cat.label || r.category}</div>
                              <div className="rule-controls">
                                {(cat.type === 'text' || cat.type === 'list') ? (
                                  cat.type === 'list'
                                    ? <input style={{ minWidth: 260, flex: 1 }} value={r.value_text || ''} placeholder="e.g. KDXR KOXC KPOU" onChange={e => updateRule(r._idx, 'value_text', e.target.value)} />
                                    : <textarea rows={Math.max(3, (r.value_text || '').split('\n').length * 2)} style={{ minWidth: 360, width: '100%', flex: 1, fontFamily: 'inherit', fontSize: 13 }} value={r.value_text || ''} placeholder="One item per line — each is shown to the pilot as a warning to accept" onChange={e => updateRule(r._idx, 'value_text', e.target.value)} />
                                ) : cat.type === 'bool' ? (
                                  <select value={r.value_num} onChange={e => updateRule(r._idx, 'value_num', parseInt(e.target.value))}>
                                    <option value={0}>Not Allowed</option>
                                    <option value={1}>Allowed</option>
                                  </select>
                                ) : (
                                  <div className="rule-input-group">
                                    <span className="rule-op">{cat.type === 'max' ? '≤' : '≥'}</span>
                                    <input type="number" value={r.value_num || ''} onChange={e => updateRule(r._idx, 'value_num', parseFloat(e.target.value) || 0)} />
                                    <span className="rule-unit">{cat.unit}</span>
                                  </div>
                                )}
                                <select className="rule-action" value={r.action} onChange={e => updateRule(r._idx, 'action', e.target.value)}
                                  style={{ color: actionColors[r.action] || '#333' }}>
                                  {Object.entries(actionLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                                <button className="btn-ghost btn-danger btn-sm" onClick={() => removeRule(r._idx)}>×</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add Rule */}
              <div className="card" style={{ marginTop: 12 }}>
                <h3>Add Rule</h3>
                <div className="add-rules-grid">
                  {Object.entries(groupLabels).map(([group, label]) => (
                    <div key={group}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>{label}</div>
                      {Object.entries(categories).filter(([_, c]) => c.group === group).map(([key, c]) => {
                        const exists = !c.multi && tierRules.some(r => r.category === key);
                        return (
                          <button key={key} className={`add-rule-btn ${exists ? 'exists' : ''}`} disabled={exists}
                            onClick={() => addRule(key)}>
                            {exists ? '✓' : '+'} {c.label}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state" style={{ padding: '60px 20px' }}>
              <span style={{ fontSize: 48 }}>📋</span>
              <h3>Select a Tier</h3>
              <p>Choose a pilot tier from the left to edit its FRAT rules.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Settings (placeholder) ──

// ── CFI Management ──
function CFIsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [cfis, setCfis] = useState([]);
  const [openCfi, setOpenCfi] = useState(null);
  const [loading, setLoading] = useState(true);

  const [fleetTypes, setFleetTypes] = useState([]);

  const load = () => {
    setLoading(true);
    apiFetch('/api/cfis', token).then(d => { setCfis(d.cfis || []); setFleetTypes(d.fleet_types || []); }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, [token]);

  const pending = cfis.filter(c => c.status === 'pending');
  const active = cfis.filter(c => c.status === 'active');

  const acceptCfi = async (linkId) => {
    try { await apiPost(`/api/cfis/${linkId}/accept`, {}, token); load(); }
    catch (err) { alert(err.message); }
  };
  const rejectCfi = async (linkId) => {
    if (!window.confirm('Reject this CFI request?')) return;
    try { await apiPost(`/api/cfis/${linkId}/reject`, {}, token); load(); }
    catch (err) { alert(err.message); }
  };
  const toggleVerify = async (linkId, currentlyVerified) => {
    try { await apiPost(`/api/cfis/${linkId}/verify`, { verified: !currentlyVerified }, token); load(); }
    catch (err) { alert(err.message); }
  };
  const unlinkCfi = async (linkId, name) => {
    if (!window.confirm(`Remove CFI ${name || ''} from your school?`)) return;
    try { await apiPost(`/api/pilots/${linkId}/unlink`, {}, token); load(); }
    catch (err) { alert(err.message); }
  };

  if (loading) return <div className="loading-state">Loading CFIs...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div><h1>CFI Management</h1><p>{active.length} active · {pending.length} pending</p></div>
      </div>

      {/* Pending Requests */}
      {pending.length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: '#f59e0b', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800 }}>{pending.length}</span>
            CFI Requests
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {pending.map(c => (
              <div key={c.link_id} className="pilot-request-card">
                <div className="pilot-info">
                  <div className="pilot-name">{c.display_name || c.username || 'Unknown'}</div>
                  <div className="pilot-meta">
                    {c.home_airport && <span>📍 {c.home_airport}</span>}
                    <span style={{ marginLeft: 8 }}>
                      {[...(c.certificates || []).filter(cert => cert === 'CFI'),
                        ...(c.ratings || []).filter(r => r === 'CFII')].join(' · ') || 'CFI'}
                    </span>
                  </div>
                  {(() => { const matched = (c.planes_i_fly || []).filter(p => fleetTypes.includes(p)); return matched.length > 0 ? (
                    <div className="pilot-meta" style={{ marginTop: 2 }}>✈️ {matched.join(', ')}</div>
                  ) : null; })()}
                  {c.cfi_will_travel && (
                    <div className="pilot-meta" style={{ marginTop: 2 }}>
                      🚗 Will travel{c.cfi_travel_distance ? ` ${c.cfi_travel_distance}mi` : ''}
                    </div>
                  )}
                </div>
                <div className="pilot-actions">
                  <button className="btn-primary btn-sm" onClick={() => acceptCfi(c.link_id)}>Accept</button>
                  <button className="btn-ghost btn-danger btn-sm" onClick={() => rejectCfi(c.link_id)}>Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active CFIs */}
      <div className="card">
        <h3>🎓 School CFIs</h3>
        {active.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No CFIs affiliated yet. CFIs can affiliate with your school from their BMAC3 profile.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>CFI</th><th>Certificates</th><th>Docs</th><th>Aircraft</th><th>Base</th><th>Available</th><th>Verified</th><th></th></tr>
              </thead>
              <tbody>
                {active.map(c => (
                  <React.Fragment key={c.link_id}>
                  <tr style={{ background: openCfi === c.link_id ? 'rgba(59,130,246,0.06)' : undefined }}>
                    <td>
                      <div className="bold" style={{ cursor: 'pointer', color: '#2563eb' }} onClick={() => setOpenCfi(openCfi === c.link_id ? null : c.link_id)}>{openCfi === c.link_id ? '▾ ' : '▸ '}{c.display_name || c.username || '—'}</div>
                      {c.cfi_will_travel && (
                        <div style={{ fontSize: 11, color: '#999' }}>🚗 Will travel{c.cfi_travel_distance ? ` ${c.cfi_travel_distance}mi` : ''}</div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(c.certificates || []).filter(cert => cert === 'CFI').map(cert => (
                          <span key={cert} style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                            background: 'rgba(59,130,246,0.12)', color: '#3b82f6' }}>{cert}</span>
                        ))}
                        {(c.ratings || []).filter(r => r === 'CFII').map(r => (
                          <span key={r} style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                            background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>{r}</span>
                        ))}
                      </div>
                    </td>
                    <td onClick={() => navigate(`/documents/${c.link_id}`)} style={{ cursor: 'pointer' }} title="Open documents"><DocsPill d={c.docs} /></td>
                    <td style={{ fontSize: 12 }}>{(c.planes_i_fly || []).filter(p => fleetTypes.includes(p)).join(', ') || '—'}</td>
                    <td style={{ fontSize: 12, fontWeight: 600 }}>{c.home_airport || '—'}</td>
                    <td>
                      <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                        background: c.cfi_available ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.1)',
                        color: c.cfi_available ? '#10b981' : '#ef4444' }}>
                        {c.cfi_available ? '✓ Available' : '✗ Unavailable'}
                      </span>
                    </td>
                    <td>
                      <button onClick={() => toggleVerify(c.link_id, c.cfi_verified)}
                        style={{ padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          border: c.cfi_verified ? '2px solid #3b82f6' : '2px dashed #ccc',
                          background: c.cfi_verified ? 'rgba(59,130,246,0.1)' : 'transparent',
                          color: c.cfi_verified ? '#3b82f6' : '#999' }}>
                        {c.cfi_verified ? '✓ Verified' : 'Verify'}
                      </button>
                    </td>
                    <td>
                      <button className="btn-ghost btn-danger btn-sm" onClick={() => unlinkCfi(c.link_id, c.display_name || c.username)}
                        style={{ fontSize: 11, padding: '3px 8px' }}>Remove</button>
                    </td>
                  </tr>
                  {openCfi === c.link_id && <tr><td colSpan={7} style={{ padding: '4px 12px 12px', background: 'rgba(59,130,246,0.03)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', margin: '6px 0 2px' }}>FLIGHTS AS INSTRUCTOR</div>
                    <CfiFlights linkId={c.link_id} apiFetch={apiFetch} useAuth={useAuth} />
                  </td></tr>}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info card */}
      <div className="card" style={{ background: 'rgba(59,130,246,0.04)', borderLeft: '4px solid #3b82f6' }}>
        <h3 style={{ fontSize: 14, marginBottom: 6 }}>ℹ️ How CFI Affiliations Work</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 6px' }}>CFIs can affiliate with your school from their BMAC3 profile under "CFI Tools → Flight School Affiliations".</p>
          <p style={{ margin: '0 0 6px' }}>Once you accept their request, they appear in your school's CFI roster. You can then <strong>verify</strong> their credentials to indicate your school has confirmed their qualifications.</p>
          <p style={{ margin: 0 }}>CFIs can affiliate with multiple schools. Their availability and travel preferences are set in their own profile.</p>
        </div>
      </div>
    </div>
  );
}


function DispatchPolicyCard({ token }) {
  const [data, setData] = useState(null);
  const [saved, setSaved] = useState(false);
  const load = useCallback(() => { apiFetch('/api/settings/dispatch-policy', token).then(setData).catch(() => {}); }, [token]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const { policy, catalog, flags_catalog, roles, role_labels, frat_ops_catalog = [], tiers = [] } = data;
  const opmap = policy.frat_operations || {};
  const setOp = (key, tierId) => { const next = { ...opmap }; if (tierId) next[key] = Number(tierId); else delete next[key]; save({ ...policy, frat_operations: next }); };
  const save = async (next) => {
    try { await apiPut('/api/settings/dispatch-policy', next, token); setSaved(true); setTimeout(() => setSaved(false), 1500); load(); }
    catch (e) { window.alert(e.message); }
  };
  const setMode = (key, mode) => save({ ...policy, modes: { ...policy.modes, [key]: mode } });
  const setFlag = (key, v) => save({ ...policy, flags: { ...policy.flags, [key]: v } });
  const toggleRole = (r) => {
    const cur = policy.override_roles || [];
    save({ ...policy, override_roles: cur.includes(r) ? cur.filter(x => x !== r) : [...cur, r] });
  };
  const modeLabel = (c, m) => (c.mode_labels && c.mode_labels[m]) || ({ block: 'Blocker', warn: 'Warning', off: 'Off' })[m] || m;
  const modeColor = (m) => m === 'block' || m === 'rules' ? '#dc2626' : m === 'warn' ? '#d97706' : '#94a3b8';
  return (
    <div className="card" style={{ marginTop: 20, padding: 16 }}>
      <h3 style={{ margin: '0 0 4px' }}>Dispatch policy {saved && <span style={{ color: '#16a34a', fontSize: 12, fontWeight: 500 }}>saved ✓</span>}</h3>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#667' }}>
        What stops a checkout (<b style={{ color: '#dc2626' }}>Blocker</b> — needs an override),
        what just warns (<b style={{ color: '#d97706' }}>Warning</b>), and what's ignored.
        "Aircraft already checked out" always blocks and isn't configurable.
      </p>
      <table className="data-table" style={{ marginBottom: 14 }}>
        <tbody>
          {catalog.map(c => (
            <tr key={c.key}>
              <td style={{ width: '60%' }}>{c.label}</td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  {c.modes.map(m => (
                    <button key={m} onClick={() => setMode(c.key, m)}
                      style={{ padding: '3px 12px', borderRadius: 14, fontSize: 12, cursor: 'pointer',
                               border: `1px solid ${policy.modes[c.key] === m ? modeColor(m) : '#d6dbe4'}`,
                               background: policy.modes[c.key] === m ? modeColor(m) + '18' : '#fff',
                               color: policy.modes[c.key] === m ? modeColor(m) : '#667',
                               fontWeight: policy.modes[c.key] === m ? 700 : 400 }}>
                      {modeLabel(c, m)}
                    </button>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#334', marginBottom: 6 }}>Waivers &amp; acknowledgments</div>
          {flags_catalog.map(f => (
            <label key={f.key} style={{ display: 'block', fontSize: 13, marginBottom: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!policy.flags[f.key]} onChange={e => setFlag(f.key, e.target.checked)} /> {f.label}
            </label>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#334', marginBottom: 6 }}>Who can override a blocker (password re-entry)</div>
          {roles.map(r => (
            <label key={r} style={{ display: 'block', fontSize: 13, marginBottom: 6, cursor: r === 'owner' ? 'default' : 'pointer', opacity: r === 'owner' ? 0.7 : 1 }}>
              <input type="checkbox" checked={(policy.override_roles || []).includes(r)} disabled={r === 'owner'} onChange={() => toggleRole(r)} /> {role_labels[r]}{r === 'owner' ? ' (always)' : ''}
            </label>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#334', marginBottom: 4 }}>FRAT by type of flight</div>
        <p style={{ margin: '0 0 8px', fontSize: 13, color: '#667' }}>
          Pick which FRAT (from the FRAT tab) applies to each kind of flight. Dispatch works out the kind from the pilot
          (student pilot / pre-solo / rated), whether an instructor is aboard, where they're going, VFR or IFR, and day or night.
          When a row is set, it replaces the pilot's own FRAT for that flight, and an instructor aboard does <b>not</b> waive it.
          "All flights" is added to every row. Leave everything blank to keep one FRAT per pilot.
        </p>
        <table className="data-table"><tbody>
          {frat_ops_catalog.map(o => (
            <tr key={o.key}>
              <td style={{ width: '45%' }}><b style={{ fontSize: 13 }}>{o.label}</b><div style={{ fontSize: 12, color: '#667' }}>{o.desc}</div></td>
              <td><select value={opmap[o.key] || ''} onChange={e => setOp(o.key, e.target.value)} style={{ width: '100%' }}>
                <option value="">— not set (use the pilot's FRAT) —</option>
                {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></td>
            </tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

// Per-user tab visibility editor (Settings staff table). null = role default.
// UI visibility only — API permissions per role are unchanged.
function TabsCell({ u, catalog, canEdit, onSave }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([]);
  if (!open) return <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
    {u.role === 'maintenance' ? 'Fleet only' : u.tabs ? `${u.tabs.length} of ${catalog.length}` : 'role default'}
    {canEdit && <button className="btn-ghost btn-sm" style={{ marginLeft: 4 }} title="Choose which tabs this user sees"
      onClick={() => { setSel(u.tabs ? [...u.tabs] : catalog.map(c => c.key)); setOpen(true); }}>✎</button>}
  </td>;
  const tog = k => setSel(sel.includes(k) ? sel.filter(x => x !== k) : [...sel, k]);
  return <td style={{ fontSize: 12 }}>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', maxWidth: 420, padding: '4px 0' }}>
      {catalog.map(c => <label key={c.key} style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}>
        <input type="checkbox" checked={sel.includes(c.key)} onChange={() => tog(c.key)} /> {c.label}
      </label>)}
    </div>
    <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
      <button className="btn-primary btn-sm" disabled={sel.length === 0} onClick={() => { onSave(sel); setOpen(false); }}>Save</button>
      <button className="btn btn-sm" onClick={() => { onSave(null); setOpen(false); }}>Use role default</button>
      <button className="btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
    </div>
  </td>;
}

// Settings -> School profile: name, home airport, logo. Owner/admin only (the
// GET 403s for other roles and the card silently doesn't render). Home airport
// is functional: dispatch METAR and local briefs key off it.
function SchoolProfileCard() {
  const { token, updateUser } = useAuth();
  const [p, setP] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { apiFetch('/api/school/profile', token).then(d => setP(d.profile)).catch(() => setP(false)); }, [token]);
  if (!p) return null;
  const onFile = (f) => {
    if (!f) return;
    const img = new Image();
    img.onload = () => {
      const sc = Math.min(1, 256 / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(img.width * sc)); c.height = Math.max(1, Math.round(img.height * sc));
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      setP(x => ({ ...x, logo_data: c.toDataURL('image/png') }));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(f);
  };
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await apiPut('/api/school/profile', { name: p.name, icao_base: p.icao_base, logo_data: p.logo_data || null }, token);
      setP(x => ({ ...x, ...r.profile }));
      updateUser({ school_name: r.profile.name, icao_base: r.profile.icao_base, school_logo: r.profile.logo_data });
      setMsg(r.warning ? { warn: `Saved — ${r.warning}` } : { ok: 'Saved' });
    } catch (e) { setMsg({ err: e.message }); }
    setBusy(false);
  };
  return <div className="card" style={{ marginBottom: 20, padding: 16 }}>
    <h3 style={{ margin: '0 0 4px' }}>School profile</h3>
    <p style={{ margin: '0 0 12px', fontSize: 13, color: '#667' }}>Shown across the portal. The home airport is also where dispatch pulls weather and centers local flight briefs.</p>
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ textAlign: 'center' }}>
        {p.logo_data ? <img src={p.logo_data} alt="logo" style={{ height: 64, maxWidth: 160, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0', padding: 4 }} />
          : <div style={{ width: 64, height: 64, borderRadius: 8, border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🏫</div>}
        <div style={{ marginTop: 6, display: 'flex', gap: 6, justifyContent: 'center' }}>
          <label className="btn-ghost btn-sm" style={{ cursor: 'pointer' }}>Upload<input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => onFile(e.target.files[0])} /></label>
          {p.logo_data && <button className="btn-ghost btn-sm" onClick={() => setP(x => ({ ...x, logo_data: null }))}>Remove</button>}
        </div>
      </div>
      <div className="form-group" style={{ margin: 0, flex: '1 1 220px' }}><label>School name</label><input value={p.name || ''} onChange={e => setP(x => ({ ...x, name: e.target.value }))} /></div>
      <div className="form-group" style={{ margin: 0, width: 140 }}><label>Home airport</label><input className="mono" value={p.icao_base || ''} onChange={e => setP(x => ({ ...x, icao_base: e.target.value.toUpperCase() }))} placeholder="KHPN" maxLength={4} /></div>
      <button className="btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
    </div>
    {msg?.ok && <p style={{ color: '#16a34a', fontSize: 13, marginTop: 8 }}>{msg.ok}</p>}
    {msg?.warn && <p style={{ color: '#d97706', fontSize: 13, marginTop: 8 }}>{msg.warn}</p>}
    {msg?.err && <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{msg.err}</p>}
  </div>;
}

function SettingsPage() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ email: '', display_name: '', role: 'cfi' });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { apiFetch('/api/settings/users', token).then(setData).catch(() => setData({ error: true })); }, [token]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!form.email.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const r = await apiPost('/api/settings/users', form, token);
      setMsg(r.warning ? { warn: r.warning, url: r.activate_url } : { ok: `Invite sent to ${r.sent_to}` });
      setForm({ email: '', display_name: '', role: 'cfi' }); load();
    } catch (e) { setMsg({ err: e.message }); }
    setBusy(false);
  };
  const patch = async (id, body) => {
    try { await apiPut(`/api/settings/users/${id}`, body, token); setMsg(null); load(); }
    catch (e) { setMsg({ err: e.message }); }
  };
  const resend = async (id) => {
    try {
      const r = await apiPost(`/api/settings/users/${id}/resend-invite`, {}, token);
      setMsg(r.warning ? { warn: r.warning, url: r.activate_url } : { ok: `Invite re-sent to ${r.sent_to}` });
    } catch (e) { setMsg({ err: e.message }); }
  };

  if (!data) return <div className="page"><div className="page-header"><h1>Settings</h1></div><p style={{ color: '#888' }}>Loading…</p></div>;
  const labels = data.role_labels || {};
  const canManage = (t) => user?.role === 'owner' || (user?.role === 'admin' && ['cfi', 'dispatcher', 'maintenance'].includes(t));
  return (
    <div className="page">
      <div className="page-header"><h1>Settings</h1><p>Staff accounts &amp; permissions</p></div>

      <SchoolProfileCard />

      <div className="card" style={{ marginBottom: 20, padding: 16 }}>
        <h3 style={{ margin: '0 0 4px' }}>Invite a staff member</h3>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#667' }}>
          They get an email link to set a password. Roles: <b>Owner</b> — everything incl. settings ·
          <b> Admin</b> — day-to-day management · <b>Instructor</b> — students, training, dispatch ·
          <b> Front desk</b> — dispatch &amp; schedule · <b>Maintenance</b> — Fleet tab only: aircraft setup, maintenance items, squawks. (Students and renters aren't staff — they connect through the app.)
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input placeholder="email@example.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={{ flex: 2, minWidth: 200 }} />
          <input placeholder="Name" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} style={{ flex: 1, minWidth: 140 }} />
          <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
            {(data.roles || []).filter(r => canManage(r)).map(r => <option key={r} value={r}>{labels[r] || r}</option>)}
          </select>
          <button className="btn btn-primary" disabled={busy} onClick={invite}>{busy ? 'Sending…' : 'Invite'}</button>
        </div>
        {msg?.ok && <p style={{ color: '#16a34a', fontSize: 13, marginTop: 8 }}>{msg.ok}</p>}
        {msg?.err && <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{msg.err}</p>}
        {msg?.warn && <p style={{ color: '#d97706', fontSize: 13, marginTop: 8 }}>{msg.warn}{msg.url && <> — share this link: <code style={{ fontSize: 11 }}>{msg.url}</code></>}</p>}
      </div>

      <div className="table-wrap"><table className="data-table">
        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Tabs</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {(data.users || []).map(u => (
            <tr key={u.id} style={{ opacity: u.active ? 1 : 0.5 }}>
              <td>{u.display_name}{u.id === user?.id ? ' (you)' : ''}</td>
              <td>{u.email}</td>
              <td>
                {canManage(u.role) && u.id !== user?.id ? (
                  <select value={u.role} onChange={e => patch(u.id, { role: e.target.value })}>
                    {(data.roles || []).filter(r => canManage(r) || r === u.role).map(r => <option key={r} value={r}>{labels[r] || r}</option>)}
                  </select>
                ) : (labels[u.role] || u.role)}
              </td>
              <TabsCell u={u} catalog={data.tabs_catalog || []} canEdit={canManage(u.role) && u.role !== 'maintenance'} onSave={t => patch(u.id, { tabs: t })} />
              <td>{!u.active ? 'Deactivated' : u.pending ? (u.invite_expires_at ? 'Invited — not yet activated' : 'Invite expired') : 'Active'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {u.active && u.pending && <button className="btn btn-sm" onClick={() => resend(u.id)}>Re-send invite</button>}{' '}
                {canManage(u.role) && u.id !== user?.id && (
                  <button className="btn btn-sm" onClick={() => patch(u.id, { active: !u.active })}>{u.active ? 'Deactivate' : 'Reactivate'}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <DispatchPolicyCard token={token} />
    </div>
  );
}

// ── Pilot Verifications Component ──
function PilotVerifications({ profile, claimedTails, verifications, linkId, token, pilotName }) {
  const [verifs, setVerifs] = useState(verifications);
  const [saving, setSaving] = useState(null);
  const [medicalType, setMedicalType] = useState('');
  const [medicalExpiry, setMedicalExpiry] = useState('');
  const [addingMedical, setAddingMedical] = useState(false);

  const MEDICAL_TYPES = ['Class 1', 'Class 2', 'Class 3', 'BasicMed'];

  const toggle = async (key, currentlyVerified) => {
    setSaving(key);
    try {
      const res = await apiPost(`/api/pilots/${linkId}/verifications`, {
        key, verified: !currentlyVerified
      }, token);
      setVerifs(res.verifications);
    } catch (e) { alert(e.message); }
    setSaving(null);
  };

  const addMedical = async () => {
    if (!medicalType || !medicalExpiry) return;
    const key = `medical:${medicalType}`;
    setSaving(key);
    try {
      const res = await apiPost(`/api/pilots/${linkId}/verifications`, {
        key, verified: true, expires: medicalExpiry
      }, token);
      setVerifs(res.verifications);
      setMedicalType(''); setMedicalExpiry(''); setAddingMedical(false);
    } catch (e) { alert(e.message); }
    setSaving(null);
  };

  const removeMedical = async (key) => {
    setSaving(key);
    try {
      const res = await apiPost(`/api/pilots/${linkId}/verifications`, { key, verified: false }, token);
      setVerifs(res.verifications);
    } catch (e) { alert(e.message); }
    setSaving(null);
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    const days30 = new Date(now.getTime() + 30 * 86400000);
    return d > now && d < days30;
  };

  const certs = profile?.certificates || [];
  const ratings = profile?.ratings || [];
  const medicals = Object.entries(verifs).filter(([k]) => k.startsWith('medical:'));

  return (
    <div className="card">
      <h3>👨‍✈️ Credentials & Verification</h3>

      {/* Certificates */}
      {certs.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Certificates</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {certs.map(c => {
              const key = `cert:${c}`;
              const v = verifs[key];
              return (
                <button key={key} onClick={() => toggle(key, !!v)} disabled={saving === key}
                  className={`verify-badge ${v ? 'verified' : 'unverified'}`}>
                  {v ? '✓' : '○'} {c}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Ratings */}
      {ratings.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Ratings</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ratings.map(r => {
              const key = `rating:${r}`;
              const v = verifs[key];
              return (
                <button key={key} onClick={() => toggle(key, !!v)} disabled={saving === key}
                  className={`verify-badge ${v ? 'verified' : 'unverified'}`}>
                  {v ? '✓' : '○'} {r}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Medical */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Medical Certificate</div>
          {!addingMedical && <button className="btn-ghost btn-sm" onClick={() => setAddingMedical(true)}>+ Add Medical</button>}
        </div>
        {medicals.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {medicals.map(([key, v]) => {
              const type = key.replace('medical:', '');
              const expired = isExpired(v.expires);
              const expiring = isExpiringSoon(v.expires);
              return (
                <div key={key} className={`medical-card ${expired ? 'expired' : expiring ? 'expiring' : 'valid'}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 700 }}>{type}</span>
                      {v.expires && (
                        <span style={{ marginLeft: 8, fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
                          Exp: {v.expires}
                          {expired && <span style={{ color: '#ef4444', fontWeight: 800, marginLeft: 6 }}>EXPIRED</span>}
                          {expiring && !expired && <span style={{ color: '#f59e0b', fontWeight: 800, marginLeft: 6 }}>EXPIRING SOON</span>}
                        </span>
                      )}
                    </div>
                    <button className="btn-ghost btn-sm btn-danger" onClick={() => removeMedical(key)}
                      disabled={saving === key}>✕</button>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                    Verified by {v.verified_by} on {new Date(v.verified_at).toLocaleDateString()}
                  </div>
                </div>
              );
            })}
          </div>
        ) : !addingMedical && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No medical on file</div>
        )}
        {addingMedical && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <select value={medicalType} onChange={e => setMedicalType(e.target.value)}
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12 }}>
              <option value="">Type...</option>
              {MEDICAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input type="date" value={medicalExpiry} onChange={e => setMedicalExpiry(e.target.value)}
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12 }} />
            <button className="btn-primary btn-sm" onClick={addMedical}
              disabled={!medicalType || !medicalExpiry || saving}>Save</button>
            <button className="btn-ghost btn-sm" onClick={() => setAddingMedical(false)}>Cancel</button>
          </div>
        )}
      </div>

      {/* Aircraft */}
      {claimedTails?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Claimed Aircraft</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {claimedTails.map(t => <span key={t} className="mono" style={{ padding: '3px 10px', background: 'var(--bg-input)', borderRadius: 6, fontSize: 12 }}>{t}</span>)}
          </div>
        </div>
      )}
      {profile?.home_airport && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>Home Airport: <strong>{profile.home_airport}</strong></div>
      )}
    </div>
  );
}

// ── Pilot Detail Page ──
function OwnerAircraftModal({ pilot, token, onClose }) {
  const [d, setD] = useState(null); const [fleetId, setFleetId] = useState(''); const [share, setShare] = useState(''); const [busy, setBusy] = useState(false);
  const load = useCallback(() => apiFetch(`/api/pilots/${pilot.id}/aircraft`, token).then(setD).catch(e => alert(e.message)), [pilot.id, token]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!fleetId) return; setBusy(true); try { await apiPost(`/api/pilots/${pilot.id}/aircraft`, { fleet_id: Number(fleetId), share_pct: share || null }, token); setFleetId(''); setShare(''); load(); } catch (e) { alert(e.message); } setBusy(false); };
  const del = async id => { try { await apiDelete(`/api/fleet-owners/${id}`, token); load(); } catch (e) { alert(e.message); } };
  const owned = d?.owned || []; const avail = (d?.fleet || []).filter(f => !owned.some(o => o.fleet_id === f.id));
  return <div className="modal-overlay" onClick={onClose}><div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
    <h3 style={{ marginTop: 0 }}>Aircraft owned — {pilot.pilot_name || pilot.pilot_username}</h3>
    <div style={{ fontSize: 12, color: '#999', marginBottom: 10 }}>Owners see a <b>My Aircraft</b> tab in PROP 1 with utilization and the maintenance schedule for their aircraft — not individual flights or who flew them.</div>
    {!d ? <div className="muted">Loading…</div> : <>
      {owned.length === 0 && <div style={{ fontSize: 13, color: '#999', marginBottom: 8 }}>No aircraft associated.</div>}
      {owned.map(o => <div key={o.id} className="row between" style={{ padding: '6px 0', borderTop: '1px solid var(--border)' }}>
        <div><b>{o.tail_number}</b> <span style={{ color: '#999', fontSize: 12 }}>{o.ac_type}{o.share_pct ? ` · ${o.share_pct}%` : ''}</span></div>
        <button className="btn-ghost btn-danger btn-sm" onClick={() => del(o.id)} style={{ fontSize: 11 }}>Remove</button>
      </div>)}
      <div className="row" style={{ gap: 8, marginTop: 12, alignItems: 'center' }}>
        <select value={fleetId} onChange={e => setFleetId(e.target.value)} style={{ flex: 1, padding: 6 }}><option value="">Add aircraft…</option>{avail.map(f => <option key={f.id} value={f.id}>{f.tail_number} · {f.ac_type}</option>)}</select>
        <input type="number" placeholder="share %" value={share} onChange={e => setShare(e.target.value)} style={{ width: 80, padding: 6 }} min="0" max="100" />
        <button className="btn btn-primary btn-sm" disabled={!fleetId || busy} onClick={add}>Add</button>
      </div>
    </>}
    <div style={{ textAlign: 'right', marginTop: 12 }}><button className="btn-ghost btn-sm" onClick={onClose}>Close</button></div>
  </div></div>;
}

function PilotMessagesCard({ linkId, token, canDelete }) {
  const [d, setD] = useState(null); const [open, setOpen] = useState({});
  const load = useCallback(() => apiFetch(`/api/pilots/${linkId}/messages`, token).then(setD).catch(() => setD({ threads: [] })), [linkId, token]);
  useEffect(() => { load(); }, [load]);
  if (!d || !canDelete) return null;
  const delMsg = async id => { if (!window.confirm('Remove this message? It will show as "removed by the school" to both people.')) return; try { await apiDelete(`/api/messages/${id}`, token); load(); } catch (e) { alert(e.message); } };
  const delThread = async t => { if (!window.confirm(`Delete the entire conversation between ${t.cfi_name} and ${t.student_name}? This cannot be undone.`)) return; try { await apiDelete(`/api/messages/threads/${t.thread_id}`, token); load(); } catch (e) { alert(e.message); } };
  return <div className="card">
    <h3>💬 Messages <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>· CFI ↔ student, kept as part of the training record</span></h3>
    {d.threads.length === 0 && <div style={{ color: '#999', fontSize: 13 }}>No conversations yet.</div>}
    {d.threads.map(t => {
      const o = open[t.thread_id] ?? false; const msgs = t.messages || [];
      return <div key={t.thread_id} style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
        <div className="row between" style={{ cursor: 'pointer' }} onClick={() => setOpen(x => ({ ...x, [t.thread_id]: !o }))}>
          <div><b>{t.cfi_name}</b> ↔ <b>{t.student_name}</b> <span style={{ fontSize: 12, color: '#999' }}>· {msgs.length} message{msgs.length === 1 ? '' : 's'}{t.last_message_at ? ` · last ${new Date(t.last_message_at).toLocaleString()}` : ''}</span></div>
          <div className="row" style={{ gap: 8 }}><button className="btn-ghost btn-danger btn-sm" onClick={e => { e.stopPropagation(); delThread(t); }} style={{ fontSize: 11 }}>Delete conversation</button><span style={{ color: '#999' }}>{o ? '▾' : '▸'}</span></div>
        </div>
        {o && <div style={{ marginTop: 8 }}>{msgs.map(m => <div key={m.id} className="row between" style={{ padding: '6px 0', borderTop: '1px dashed var(--border)', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#999' }}>{m.sender_user_id === t.cfi_user_id ? t.cfi_name : t.student_name} · {new Date(m.sent_at).toLocaleString()}{m.read_at ? ' · seen' : ''}{m.flight_sid ? ' · ✈️ flight attached' : ''}</div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', opacity: m.deleted ? .5 : 1 }}>{m.deleted ? <i>removed</i> : m.body}</div>
          </div>
          {!m.deleted && <button className="btn-ghost btn-danger btn-sm" onClick={() => delMsg(m.id)} style={{ fontSize: 11, flexShrink: 0 }}>Remove</button>}
        </div>)}</div>}
      </div>;
    })}
  </div>;
}

function PilotDetailPage() {
  const { linkId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch(`/api/pilots/${linkId}/detail`, token).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [linkId, token]);

  if (loading) return <div className="loading-state">Loading pilot...</div>;
  if (!data) return <div className="empty-state"><h3>Pilot not found</h3></div>;

  const gradeColors = { A: '#10b981', B: '#3b82f6', C: '#f59e0b', D: '#ef4444', F: '#991b1b' };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-ghost" onClick={() => navigate('/pilots')} style={{ padding: '4px 8px' }}>← Back</button>
            <h1>{data.pilot?.pilot_name || 'Unknown Pilot'}</h1>
          </div>
          <p>{data.pilot?.tier_name || 'No tier'} · {data.pilot?.username ? `@${data.pilot.username}` : ''}</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-value">{data.total_approaches}</div><div className="stat-label">Total Approaches</div></div>
        <div className="stat-card"><div className="stat-value">{data.avg_score}%</div><div className="stat-label">Avg Score</div></div>
        <div className="stat-card"><div className="stat-value">{data.flights_90d}</div><div className="stat-label">Flights (90d)</div></div>
        <div className="stat-card"><div className="stat-value">{data.claimed_tails?.length || 0}</div><div className="stat-label">Aircraft</div></div>
      </div>

      {/* Profile & Verifications */}
      <PilotVerifications
        profile={data.profile}
        claimedTails={data.claimed_tails}
        verifications={data.verifications || {}}
        linkId={linkId}
        token={token}
        pilotName={data.pilot?.pilot_name}
      />

      {/* Skills Summary */}
      {Object.keys(data.skills_summary || {}).length > 0 && (
        <div className="card">
          <h3>🏅 Skills (from scored approaches)</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(data.skills_summary).map(([skill, info]) => (
              <div key={skill} style={{ padding: '6px 12px', background: 'rgba(37,99,235,0.06)', borderRadius: 8, fontSize: 12 }}>
                <span style={{ fontWeight: 700 }}>{skill}</span>
                <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>×{info.count}</span>
                {info.values.length > 0 && (
                  <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>
                    (avg {Math.round(info.values.reduce((a, b) => a + b, 0) / info.values.length)})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <PilotDocumentsCard linkId={linkId} apiFetch={apiFetch} apiPut={apiPut} useAuth={useAuth} />
      <TrainingProgramCard linkId={linkId} apiFetch={apiFetch} apiPost={apiPost} apiPut={apiPut} apiDelete={apiDelete} useAuth={useAuth} />
      <PilotMessagesCard linkId={linkId} token={token} canDelete={['owner', 'admin'].includes(user?.role)} />

      {/* Recent Flights — V5 shim rows; opens the activities/debrief flight view */}
      <div className="card">
        <h3>✈️ Recent Flights</h3>
        {data.recent?.length ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Tail</th><th>Route</th><th>Date</th><th>Role</th><th>Landings</th><th>Activities</th><th>Arrival</th><th>Debrief</th></tr></thead>
              <tbody>
                {data.recent.map(r => (
                  <tr key={r.id} onClick={() => navigate(`/flights/${r.id}`)} style={{ cursor: 'pointer' }}>
                    <td className="mono bold">{r.callsign}</td>
                    <td>{r.dep_airport ? `${r.dep_airport} → ` : ''}{r.arr_airport || '?'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{r.flight_date}<br/><span style={{ color: '#999', fontSize: 11 }}>{r.landing_time ? new Date(r.landing_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' }) + 'Z' : ''}</span></td>
                    <td style={{ fontSize: 12 }}>{r.role || <span style={{ color: '#bbb' }}>unclaimed</span>}</td>
                    <td className="mono">{r.landings != null ? r.landings : '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      {r.act_status === 'done' ? <span style={{ color: '#0d9488', fontWeight: 700 }}>{r.maneuver_count} maneuver{r.maneuver_count === 1 ? '' : 's'}</span>
                        : r.act_status === 'pending' ? <span style={{ color: '#b45309' }}>⏳ analyzing</span>
                        : r.act_status === 'error' ? <span style={{ color: '#dc2626' }}>no track</span>
                        : <span style={{ color: '#bbb' }}>—</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}><span className="mono">{r.runway_id ? `RWY ${r.runway_id}` : ''}</span> <span className="grade-badge" style={{ background: gradeColors[r.grade], fontSize: 10, padding: '1px 6px' }}>{r.grade}</span></td>
                    <td>{r.has_debrief > 0 ? <span title="Debrief written">📝</span> : <span style={{ color: '#ddd' }}>·</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No claimed flights — this list matches the pilot's My Flights in the app.</div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>Flights the pilot claimed in the app — the same basis as their proficiency tracking.</div>
      </div>

      {data.unclaimed?.length > 0 && (
        <details className="card" style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>Unclaimed flights on fleet aircraft this pilot follows ({data.unclaimed.length})</summary>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 10px' }}>Not part of the pilot's record — nobody has claimed these in the app. Shown so you can spot flights the pilot forgot to claim.</div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Tail</th><th>Arrival</th><th>Date</th><th>Activities</th></tr></thead>
              <tbody>
                {data.unclaimed.map(r => (
                  <tr key={r.id} onClick={() => navigate(`/flights/${r.id}`)} style={{ cursor: 'pointer' }}>
                    <td className="mono bold">{r.callsign}</td>
                    <td>{r.arr_airport || '?'} <span className="mono">{r.runway_id ? `RWY ${r.runway_id}` : ''}</span></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{r.flight_date} <span style={{ color: '#999', fontSize: 11 }}>{r.landing_time ? new Date(r.landing_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' }) + 'Z' : ''}</span></td>
                    <td style={{ fontSize: 12 }}>{r.act_status === 'done' ? `${r.maneuver_count} maneuver${r.maneuver_count === 1 ? '' : 's'}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

// ── Score Detail Page ──
function ScoreDetailPage() {
  const { scoreId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    apiFetch(`/api/scores/${scoreId}`, token).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [scoreId, token]);

  // Load Leaflet CSS + JS
  useEffect(() => {
    if (document.getElementById('leaflet-css')) { setMapReady(true); return; }
    const css = document.createElement('link');
    css.id = 'leaflet-css'; css.rel = 'stylesheet';
    css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(css);
    const js = document.createElement('script');
    js.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    js.onload = () => setMapReady(true);
    document.head.appendChild(js);
  }, []);

  // Render map when data + leaflet ready
  useEffect(() => {
    if (!mapReady || !data?.score?.track_json) return;
    const track = typeof data.score.track_json === 'string' ? JSON.parse(data.score.track_json) : data.score.track_json;
    if (!track.length) return;
    const el = document.getElementById('score-map');
    if (!el || el._leaflet_id) return;
    const L = window.L;
    const coords = track.filter(p => p.latitude && p.longitude).map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]);
    if (!coords.length) return;
    const map = L.map(el).fitBounds(L.latLngBounds(coords).pad(0.15));
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Esri', maxZoom: 19
    }).addTo(map);
    // Color by altitude
    const alts = track.map(p => p.altitude || 0);
    const minAlt = Math.min(...alts), maxAlt = Math.max(...alts);
    for (let i = 1; i < coords.length; i++) {
      const pct = maxAlt > minAlt ? (alts[i] - minAlt) / (maxAlt - minAlt) : 0.5;
      const r = Math.round(255 * (1 - pct)), g = Math.round(200 * pct), b = Math.round(255 * pct);
      L.polyline([coords[i - 1], coords[i]], { color: `rgb(${r},${g},${b})`, weight: 3, opacity: 0.9 }).addTo(map);
    }
    // Runway threshold marker
    const rwy = data.score.runway_json;
    if (rwy) {
      const rj = typeof rwy === 'string' ? JSON.parse(rwy) : rwy;
      if (rj.threshold_lat && rj.threshold_lon) {
        L.circleMarker([parseFloat(rj.threshold_lat), parseFloat(rj.threshold_lon)], {
          radius: 8, color: '#f00', fillColor: '#f00', fillOpacity: 0.8, weight: 2
        }).addTo(map).bindPopup(`RWY ${data.score.runway_id}`);
      }
    }
    // Start/end markers
    L.circleMarker(coords[0], { radius: 5, color: '#0f0', fillColor: '#0f0', fillOpacity: 0.8 }).addTo(map).bindPopup('Start');
    L.circleMarker(coords[coords.length - 1], { radius: 5, color: '#f80', fillColor: '#f80', fillOpacity: 0.8 }).addTo(map).bindPopup('End');
  }, [mapReady, data]);

  if (loading) return <div className="loading-state">Loading score...</div>;
  if (!data?.score) return <div className="empty-state"><h3>Score not found</h3></div>;

  const s = data.score;
  const gradeColors = { A: '#10b981', B: '#3b82f6', C: '#f59e0b', D: '#ef4444', F: '#991b1b' };
  const skills = s.skills_json ? (typeof s.skills_json === 'string' ? JSON.parse(s.skills_json) : s.skills_json) : [];
  const catPct = (score, max) => max > 0 ? Math.round((score / max) * 100) : 0;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-ghost" onClick={() => navigate(-1)} style={{ padding: '4px 8px' }}>← Back</button>
            <h1>{s.callsign} · {s.dep_airport} → {s.arr_airport}</h1>
          </div>
          <p>RWY {s.runway_id} · {s.flight_date} · {s.ac_type}</p>
        </div>
        <span className="grade-badge" style={{ background: gradeColors[s.grade], fontSize: 28, padding: '8px 20px', borderRadius: 12 }}>{s.grade}</span>
      </div>

      {/* Overall Score */}
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-value" style={{ color: gradeColors[s.grade] }}>{s.percentage}%</div><div className="stat-label">Overall Score</div></div>
        <div className="stat-card"><div className="stat-value">{s.avg_speed_kt || '—'}</div><div className="stat-label">Avg Speed (kt)</div></div>
        <div className="stat-card"><div className="stat-value">{s.crosswind_kt || '—'}</div><div className="stat-label">Crosswind (kt)</div></div>
        <div className="stat-card"><div className="stat-value">{s.threshold_agl_ft || '—'}</div><div className="stat-label">Threshold AGL (ft)</div></div>
      </div>

      {/* Category Breakdown */}
      <div className="card">
        <h3>📊 Score Breakdown</h3>
        <div className="score-bars">
          {[
            { label: 'Vertical Profile', score: s.vertical_score, max: s.vertical_max, icon: '📐' },
            { label: 'Horizontal Track', score: s.horizontal_score, max: s.horizontal_max, icon: '↔️' },
            { label: 'Speed Control', score: s.speed_score, max: s.speed_max, icon: '⚡' },
            { label: 'Stabilized', score: s.stabilized_score, max: s.stabilized_max, icon: '✅' },
            { label: 'Bank Angle', score: s.bank_score, max: s.bank_max, icon: '🔄' },
          ].map(cat => {
            const pct = catPct(cat.score, cat.max);
            const color = pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
            return (
              <div key={cat.label} className="score-bar-row">
                <div className="score-bar-label">{cat.icon} {cat.label}</div>
                <div className="score-bar-track">
                  <div className="score-bar-fill" style={{ width: `${pct}%`, background: color }}></div>
                </div>
                <div className="score-bar-pct" style={{ color }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Wind & Conditions */}
      <div className="card">
        <h3>🌦️ Conditions</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13 }}>
          {s.wind_speed_kt != null && <div><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Wind:</span> {s.wind_dir}° @ {s.wind_speed_kt}kt{s.wind_gust_kt ? ` G${s.wind_gust_kt}` : ''}</div>}
          {s.crosswind_kt != null && <div><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Crosswind:</span> {s.crosswind_kt}kt</div>}
          {s.max_crosstrack_ft != null && <div><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Max Crosstrack:</span> {s.max_crosstrack_ft}ft</div>}
          {s.max_bank_angle != null && <div><span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Max Bank:</span> {s.max_bank_angle}°</div>}
        </div>
        {skills.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {skills.map((sk, i) => (
              <span key={i} style={{ padding: '3px 10px', background: 'rgba(37,99,235,0.08)', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>
                {sk.skill}{sk.detail ? `: ${sk.detail}` : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Map */}
      <div className="card">
        <h3>🗺️ Approach Track</h3>
        <div id="score-map" style={{ height: 400, borderRadius: 8, background: '#1a1a2e' }}></div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, display: 'flex', gap: 16 }}>
          <span>🟢 Start</span><span>🔴 Threshold</span><span>🟠 End</span>
          <span>Color = altitude (blue=high, red=low)</span>
        </div>
      </div>
    </div>
  );
}





// ── Training / Curriculum ──────────────────────────────────────────────────
function TrainingCoursesPage() {
  const [tab, setTab] = useState('acs');
  const tabs = [
    { id:'acs',         label:'ACS Standards',   icon:'📋' },
    { id:'courses',     label:'Course Catalogue', icon:'📚' },
    { id:'assignments', label:'Assignments',      icon:'🎯' },
  ];
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>📋 Curriculum</h1>
        <p className="subtitle">ACS standards, course catalogue, and tier assignments</p>
      </div>
      <div style={{ display:'flex', gap:0, marginBottom:20, borderBottom:'2px solid #e2e8f0' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:'10px 20px', border:'none',
            borderBottom: tab===t.id ? '2px solid #2563eb' : '2px solid transparent',
            background:'none', fontSize:14, fontWeight: tab===t.id ? 600 : 400,
            color: tab===t.id ? '#2563eb' : '#64748b', cursor:'pointer', marginBottom:-2
          }}>{t.icon} {t.label}</button>
        ))}
      </div>
      {tab === 'acs'         && <ACSStandardsTab />}
      {tab === 'courses'     && <CourseCatalogueTab />}
      {tab === 'assignments' && <AssignmentsTab />}
    </div>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────
const CERT_ORDER = ['Private','Instrument','Commercial','CFI','Mission Specific','AC Type','Custom'];
const CERT_COLORS = {
  Private:'#2563eb', Instrument:'#7c3aed', Commercial:'#059669',
  CFI:'#d97706', 'Mission Specific':'#a855f7', 'AC Type':'#0891b2', Custom:'#64748b'
};
function CertPill({ cert }) {
  const color = CERT_COLORS[cert] || '#64748b';
  return <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:10,
    background: color+'18', color, border:`1px solid ${color}33`, letterSpacing:'0.04em' }}>{cert}</span>;
}
function SourceBadge({ source }) {
  if (source === 'school') return <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px',
    borderRadius:4, background:'#fef3c7', color:'#d97706', border:'1px solid #fde68a' }}>School</span>;
  return <span style={{ fontSize:10, color:'#94a3b8' }}>Platform</span>;
}
function DocsPill({ d }) {
  if (!d) return <span style={{ color: '#999' }}>—</span>;
  const st = d.none ? { l: 'n/a', c: '#64748b', bg: '#f1f5f9' } : d.ok ? (d.expiring ? { l: `OK · ${d.expiring} expiring`, c: '#92400e', bg: '#fef3c7' } : { l: 'OK', c: '#065f46', bg: '#d1fae5' })
    : { l: [d.missing ? `${d.missing} missing` : null, d.expired ? `${d.expired} expired` : null].filter(Boolean).join(', '), c: '#991b1b', bg: '#fee2e2' };
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, color: st.c, background: st.bg, whiteSpace: 'nowrap' }}>{st.l}</span>;
}
function TierPills({ tiers }) {
  if (!tiers || tiers.length === 0)
    return <span style={{ fontSize:11, color:'#94a3b8' }}>—</span>;
  return <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
    {tiers.map((t,i) => (
      <span key={i} style={{ fontSize:10, padding:'2px 6px', borderRadius:4,
        background: t.tier_id === null ? '#f0fdf4' : '#eff6ff',
        color: t.tier_id === null ? '#16a34a' : '#2563eb',
        border: `1px solid ${t.tier_id === null ? '#bbf7d0' : '#bfdbfe'}` }}>
        {t.tier_name || 'All Tiers'}
      </span>
    ))}
  </div>;
}

// ── ACS Standards Tab ─────────────────────────────────────────────────────
function ACSStandardsTab() {
  const { token } = useAuth();
  const [items, setItems]               = useState([]);
  const [tiers, setTiers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [certFilter, setCertFilter]     = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [collapsed, setCollapsed]       = useState({});
  const [selected, setSelected]         = useState(null);   // item open in side panel
  const [adding, setAdding]             = useState(false);
  const [assignModal, setAssignModal]   = useState(null);   // { ids:[], mode:'standard' }
  const [editFreq, setEditFreq]         = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (certFilter !== 'all') q.set('cert', certFilter);
      if (sourceFilter !== 'all') q.set('source', sourceFilter);
      const data = await apiFetch(`/api/training/acs?${q}`, token);
      setItems(data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };
  const toggleCourseExemptInCat = async (courseId, tierId, targetRole, currentExempt) => {
    await apiPost('/api/training/courses/assign', { course_ids:[courseId], tier_id:tierId, target_role:targetRole, scoring_exempt:!currentExempt }, token);
    load();
  };
  useEffect(() => { apiFetch('/api/frat/tiers', token).then(d => setTiers(d.tiers || [])).catch(()=>{}); }, [token]);
  useEffect(() => { load(); }, [certFilter, sourceFilter, token]);

  const grouped = items.reduce((acc, a) => {
    const cert = a.cert_level || 'Custom';
    const cat  = a.category   || 'Uncategorized';
    if (!acc[cert]) acc[cert] = {};
    if (!acc[cert][cat]) acc[cert][cat] = [];
    acc[cert][cat].push(a);
    return acc;
  }, {});
  const sortedCerts = Object.keys(grouped).sort(
    (a,b) => (CERT_ORDER.indexOf(a)+1||99) - (CERT_ORDER.indexOf(b)+1||99)
  );

  const toggleCat = key => setCollapsed(p => ({ ...p, [key]: !p[key] }));

  const saveFreq = async (item, val) => {
    const days = parseInt(val, 10);
    if (!days || days < 1) return;
    setItems(prev => prev.map(i => i.id === item.id ? {...i, frequency_days: days} : i));
    setEditFreq(p => { const n={...p}; delete n[item.id]; return n; });
    await apiPut(`/api/training/acs/${item.id}`, { frequency_days: days }, token);
  };

  const toggleACSExempt = async (stdId, tierId, targetRole, currentExempt) => {
    await apiPost('/api/training/acs/assign', { standard_ids:[stdId], tier_id:tierId, target_role:targetRole, scoring_exempt:!currentExempt }, token);
    load();
  };
  const toggleStatus = async (item) => {
    const next = item.status === 'active' ? 'inactive' : 'active';
    setItems(prev => prev.map(i => i.id === item.id ? {...i, status: next} : i));
    await apiPut(`/api/training/acs/${item.id}`, { status: next }, token);
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.task}"?`)) return;
    await apiDelete(`/api/training/acs/${item.id}`, token);
    setSelected(null); load();
  };

  const saveNew = async (d) => {
    await apiPost('/api/training/acs', d, token);
    setAdding(false); load();
  };

  const saveEdit = async (d) => {
    await apiPut(`/api/training/acs/${selected.id}`, d, token);
    setSelected(null); load();
  };

  // Bulk assign selected category to a tier
  const assignCategory = (certKey, catKey) => {
    const ids = grouped[certKey][catKey].map(i => i.id);
    setAssignModal({ ids, label: catKey });
  };

  return (
    <div style={{ display:'flex', gap:20 }}>
      <div style={{ flex:1, minWidth:0 }}>
        {/* Toolbar */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:8 }}>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {/* Cert filter */}
            {['all',...CERT_ORDER].map(c => (
              <button key={c} onClick={() => setCertFilter(c)} style={{
                padding:'4px 12px', borderRadius:20, fontSize:12, cursor:'pointer', border:'1px solid',
                borderColor: certFilter===c ? '#2563eb' : '#e2e8f0',
                background: certFilter===c ? '#eff6ff' : '#fff',
                color: certFilter===c ? '#2563eb' : '#64748b', fontWeight: certFilter===c ? 600 : 400
              }}>{c==='all' ? 'All Certs' : c}</button>
            ))}
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {['all','platform','school'].map(s => (
              <button key={s} onClick={() => setSourceFilter(s)} style={{
                padding:'4px 12px', borderRadius:20, fontSize:12, cursor:'pointer', border:'1px solid',
                borderColor: sourceFilter===s ? '#7c3aed' : '#e2e8f0',
                background: sourceFilter===s ? '#f5f3ff' : '#fff',
                color: sourceFilter===s ? '#7c3aed' : '#64748b', fontWeight: sourceFilter===s ? 600 : 400
              }}>{s==='all' ? 'All Sources' : s==='platform' ? 'Platform' : 'School'}</button>
            ))}
            <button onClick={() => { setAdding(true); setSelected(null); }} style={{
              padding:'6px 16px', borderRadius:8, background:'#2563eb', color:'#fff',
              border:'none', fontSize:13, fontWeight:600, cursor:'pointer'
            }}>+ Add Standard</button>
          </div>
        </div>

        {loading && <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>Loading…</div>}

        {sortedCerts.map(cert => (
          <div key={cert} style={{ marginBottom:20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <CertPill cert={cert} />
              <span style={{ fontSize:12, color:'#94a3b8' }}>{Object.values(grouped[cert]).flat().length} standards</span>
              <div style={{ flex:1, height:1, background:'#e2e8f0' }} />
            </div>
            {Object.keys(grouped[cert]).sort().map(cat => {
              const catKey = `${cert}::${cat}`;
              const isCollapsed = collapsed[catKey];
              const catItems = grouped[cert][cat];
              const activeCount = catItems.filter(i => i.status === 'active').length;
              const hasSchoolItems = catItems.some(i => i.source === 'school');
              return (
                <div key={cat} style={{ background:'#fff', border:'1px solid #e2e8f0',
                  borderRadius:8, marginBottom:6, overflow:'hidden' }}>
                  <div onClick={() => toggleCat(catKey)} style={{
                    display:'flex', alignItems:'center', gap:8, padding:'10px 14px',
                    cursor:'pointer', background:'#f8fafc', userSelect:'none'
                  }}>
                    <span style={{ fontSize:11, color:'#94a3b8', width:12 }}>{isCollapsed ? '▶' : '▾'}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:'#1e293b', flex:1 }}>{cat}</span>
                    {hasSchoolItems && <SourceBadge source="school" />}
                    <span style={{ fontSize:11, color:'#94a3b8' }}>{activeCount}/{catItems.length} active</span>
                    <button onClick={e => { e.stopPropagation(); assignCategory(cert, cat); }}
                      style={{ padding:'3px 10px', borderRadius:6, fontSize:11, fontWeight:600,
                        background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', cursor:'pointer' }}>
                      Assign to Tier
                    </button>
                  </div>
                  {!isCollapsed && (
                    <>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 90px 80px 80px 100px 120px',
                        padding:'6px 14px', fontSize:10, fontWeight:700, color:'#94a3b8',
                        textTransform:'uppercase', letterSpacing:'0.07em', borderBottom:'1px solid #e2e8f0' }}>
                        <span>Task</span><span>Source</span><span>Type</span><span>Freq</span>
                        <span>Assigned</span><span style={{textAlign:'right'}}>Status</span>
                      </div>
                      {catItems.map((a, idx) => {
                        const freqVal = editFreq[a.id] !== undefined ? editFreq[a.id] : String(a.frequency_days);
                        return (
                          <div key={a.id} onClick={() => setSelected(selected?.id===a.id ? null : a)}
                            style={{ display:'grid', gridTemplateColumns:'1fr 90px 80px 80px 100px 120px',
                              alignItems:'center', padding:'9px 14px',
                              borderBottom: idx < catItems.length-1 ? '1px solid #f1f5f9' : 'none',
                              background: selected?.id===a.id ? '#eff6ff' : a.status==='inactive' ? '#f8fafc' : '#fff',
                              opacity: a.status==='inactive' ? 0.6 : 1, cursor:'pointer' }}>
                            <span style={{ fontSize:13, fontWeight:500, color:'#1e293b', paddingRight:8 }}>{a.task}</span>
                            <span onClick={e=>e.stopPropagation()}><SourceBadge source={a.source} /></span>
                            <span style={{ fontSize:11, color:'#64748b', textTransform:'capitalize' }}>{a.item_type}</span>
                            <div onClick={e=>e.stopPropagation()} style={{ display:'flex', alignItems:'center', gap:3 }}>
                              <input type="number" min="1" value={freqVal}
                                onChange={e => setEditFreq(p => ({...p, [a.id]: e.target.value}))}
                                onFocus={e => { e.target.select(); setEditFreq(p=>({...p,[a.id]:String(a.frequency_days)})); }}
                                onBlur={e => saveFreq(a, e.target.value)}
                                onKeyDown={e => { if(e.key==='Enter') e.target.blur(); if(e.key==='Escape'){ setEditFreq(p=>{const n={...p};delete n[a.id];return n;}); e.target.blur(); }}}
                                style={{ width:44, padding:'2px 5px', borderRadius:5, fontSize:11, fontWeight:600,
                                  background: editFreq[a.id]!==undefined ? '#eff6ff' : '#f8fafc',
                                  border:`1px solid ${editFreq[a.id]!==undefined ? '#2563eb' : '#e2e8f0'}`,
                                  color:'#1e293b', textAlign:'center', outline:'none' }} />
                              <span style={{ fontSize:10, color:'#94a3b8' }}>d</span>
                            </div>
                            <div onClick={e=>e.stopPropagation()}>
                              <TierPills tiers={a.assigned_tiers} />
                              {a.assigned_tiers.filter(t => t.target_role !== 'cfi').map((t, i) => (
                                <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:3, marginTop:3, marginRight:3 }}>
                                  <button onClick={() => toggleACSExempt(a.id, t.tier_id, t.target_role, t.scoring_exempt)}
                                    title={t.scoring_exempt ? 'Click to count toward score' : 'Click to make info only'}
                                    style={{ padding:'1px 7px', borderRadius:4, fontSize:10, fontWeight:700, cursor:'pointer',
                                      background: t.scoring_exempt ? 'rgba(248,113,113,0.1)' : '#f0fdf4',
                                      color: t.scoring_exempt ? '#f87171' : '#16a34a',
                                      border: `1px solid ${t.scoring_exempt ? '#f8717133' : '#bbf7d0'}` }}>
                                    {t.scoring_exempt ? '👁 info only' : '✓ scored'}
                                  </button>
                                  {!t.scoring_exempt && (
                                    <span style={{ padding:'1px 5px', borderRadius:4, fontSize:10, fontWeight:800,
                                      background:'rgba(167,139,250,0.12)', color:'#8b5cf6', border:'1px solid #a78bfa44' }}>
                                      {t.point_value || 1}pt
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                            <div onClick={e=>e.stopPropagation()} style={{ display:'flex', justifyContent:'flex-end', gap:4 }}>
                              <button onClick={() => setAssignModal({ ids:[a.id], label:a.task, showPoints:true })}
                                style={{ padding:'3px 8px', borderRadius:5, fontSize:10, fontWeight:600, cursor:'pointer',
                                  background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe' }}>Assign</button>
                              <button onClick={() => toggleStatus(a)}
                                style={{ padding:'3px 10px', borderRadius:5, fontSize:11, fontWeight:700, cursor:'pointer',
                                  border:`1px solid ${a.status==='active' ? '#bbf7d0' : '#e2e8f0'}`,
                                  background: a.status==='active' ? '#f0fdf4' : '#f8fafc',
                                  color: a.status==='active' ? '#16a34a' : '#94a3b8', minWidth:60 }}>
                                {a.status==='active' ? '● Active' : '○ Off'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Side panel */}
      {(selected || adding) && (
        <ACSEditPanel
          item={adding ? null : selected}
          onClose={() => { setSelected(null); setAdding(false); }}
          onSave={adding ? saveNew : saveEdit}
          onDelete={selected?.source==='school' ? () => deleteItem(selected) : null}
        />
      )}

      {/* Assign to tier modal */}
      {assignModal && (
        <AssignToTierModal
          tiers={tiers}
          label={assignModal.label}
          showPoints={assignModal.showPoints || false}
          onAssign={async (tierId, targetRole, scoringExempt, pointValue) => {
            await apiPost('/api/training/acs/assign', { standard_ids: assignModal.ids, tier_id: tierId, target_role: targetRole, scoring_exempt: scoringExempt, point_value: pointValue }, token);
            setAssignModal(null); load();
          }}
          onClose={() => setAssignModal(null)}
        />
      )}
    </div>
  );
}

function ACSEditPanel({ item, onClose, onSave, onDelete }) {
  const isNew = !item;
  const [form, setForm] = useState({
    task: item?.task || '',
    category: item?.category || '',
    cert_level: item?.cert_level || 'Mission Specific',
    item_type: item?.item_type || 'maneuver',
    frequency_days: item?.frequency_days || 90,
    acs_ref: item?.acs_ref || '',
    description: item?.description || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(p => ({...p, [k]:v}));
  const canEdit = isNew || item?.source === 'school';

  const handle = async () => {
    setSaving(true);
    try { await onSave(form); } catch(e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ width:300, background:'#fff', border:'1px solid #e2e8f0', borderRadius:10,
      padding:20, height:'fit-content', position:'sticky', top:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:14, fontWeight:700, color:'#1e293b' }}>
          {isNew ? 'New Standard' : canEdit ? 'Edit Standard' : 'View Standard'}
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:18, cursor:'pointer' }}>✕</button>
      </div>
      {!canEdit && <div style={{ marginBottom:12, padding:'8px 10px', background:'#fffbeb', borderRadius:6,
        fontSize:12, color:'#92400e', border:'1px solid #fde68a' }}>
        Platform standard — only frequency and status are editable.
      </div>}
      {[
        { label:'Task', key:'task', type:'text', disabled:!canEdit },
        { label:'Category', key:'category', type:'text', disabled:!canEdit },
        { label:'ACS Ref', key:'acs_ref', type:'text', disabled:!canEdit },
        { label:'Frequency (days)', key:'frequency_days', type:'number', disabled:false },
      ].map(f => (
        <div key={f.key} style={{ marginBottom:12 }}>
          <div style={{ fontSize:11, color:'#64748b', marginBottom:4, fontWeight:600 }}>{f.label}</div>
          <input type={f.type} value={form[f.key]} disabled={f.disabled}
            onChange={e => set(f.key, f.type==='number' ? parseInt(e.target.value)||0 : e.target.value)}
            style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid #e2e8f0',
              fontSize:13, color:'#1e293b', background: f.disabled ? '#f8fafc' : '#fff',
              boxSizing:'border-box' }} />
        </div>
      ))}
      {canEdit && (
        <>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, color:'#64748b', marginBottom:4, fontWeight:600 }}>Certificate Level</div>
            <select value={form.cert_level} onChange={e=>set('cert_level',e.target.value)}
              style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid #e2e8f0', fontSize:13 }}>
              {CERT_ORDER.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, color:'#64748b', marginBottom:4, fontWeight:600 }}>Type</div>
            <select value={form.item_type} onChange={e=>set('item_type',e.target.value)}
              style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid #e2e8f0', fontSize:13 }}>
              {['maneuver','knowledge','risk','mission_specific','custom'].map(t =>
                <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </>
      )}
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={handle} disabled={saving} style={{
          flex:1, padding:'8px', borderRadius:8, background:'#2563eb', color:'#fff',
          border:'none', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
        </button>
        {onDelete && (
          <button onClick={onDelete} style={{ padding:'8px 14px', borderRadius:8, background:'#fef2f2',
            color:'#dc2626', border:'1px solid #fecaca', fontSize:13, cursor:'pointer' }}>Del</button>
        )}
      </div>
    </div>
  );
}

// ── Course Catalogue Tab ──────────────────────────────────────────────────
function CourseCatalogueTab() {
  const { token } = useAuth();
  const [courses, setCourses]           = useState([]);
  const [tiers, setTiers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selected, setSelected]         = useState(null);
  const [adding, setAdding]             = useState(false);
  const [assignModal, setAssignModal]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (search) q.set('q', search);
      if (sourceFilter !== 'all') q.set('source', sourceFilter);
      const data = await apiFetch(`/api/training/school-courses?${q}`, token);
      setCourses(data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };
  const toggleCourseExemptInCat = async (courseId, tierId, targetRole, currentExempt) => {
    await apiPost('/api/training/courses/assign', { course_ids:[courseId], tier_id:tierId, target_role:targetRole, scoring_exempt:!currentExempt }, token);
    load();
  };
  useEffect(() => { apiFetch('/api/frat/tiers', token).then(d => setTiers(d.tiers || [])).catch(()=>{}); }, [token]);
  useEffect(() => { load(); }, [search, sourceFilter, token]);

  const deleteCourse = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    await apiDelete(`/api/training/school-courses/${c.id}`, token);
    setSelected(null); load();
  };

  return (
    <div style={{ display:'flex', gap:20 }}>
      <div style={{ flex:1, minWidth:0 }}>
        {/* Toolbar */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:8 }}>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input placeholder="Search courses…" value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding:'7px 12px', borderRadius:8, border:'1px solid #e2e8f0',
                fontSize:13, width:220, outline:'none' }} />
            {['all','platform','school'].map(s => (
              <button key={s} onClick={() => setSourceFilter(s)} style={{
                padding:'4px 12px', borderRadius:20, fontSize:12, cursor:'pointer', border:'1px solid',
                borderColor: sourceFilter===s ? '#7c3aed' : '#e2e8f0',
                background: sourceFilter===s ? '#f5f3ff' : '#fff',
                color: sourceFilter===s ? '#7c3aed' : '#64748b', fontWeight: sourceFilter===s ? 600 : 400
              }}>{s==='all' ? 'All' : s==='platform' ? 'Platform' : 'School'}</button>
            ))}
          </div>
          <button onClick={() => { setAdding(true); setSelected(null); }} style={{
            padding:'6px 16px', borderRadius:8, background:'#2563eb', color:'#fff',
            border:'none', fontSize:13, fontWeight:600, cursor:'pointer' }}>+ Add Course</button>
        </div>

        {loading && <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>Loading…</div>}

        <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 100px 80px 100px 110px 120px',
            padding:'8px 14px', fontSize:10, fontWeight:700, color:'#94a3b8',
            textTransform:'uppercase', letterSpacing:'0.07em', borderBottom:'1px solid #e2e8f0', background:'#f8fafc' }}>
            <span>Title</span><span>Provider</span><span>Source</span>
            <span>Freq</span><span>Assigned</span><span style={{textAlign:'right'}}>Actions</span>
          </div>
          {courses.map((c, idx) => (
            <div key={c.id} onClick={() => setSelected(selected?.id===c.id ? null : c)}
              style={{ display:'grid', gridTemplateColumns:'1fr 100px 80px 100px 110px 120px',
                alignItems:'center', padding:'10px 14px',
                borderBottom: idx < courses.length-1 ? '1px solid #f1f5f9' : 'none',
                background: selected?.id===c.id ? '#eff6ff' : '#fff', cursor:'pointer' }}>
              <div>
                <div style={{ fontSize:13, fontWeight:500, color:'#1e293b' }}>{c.title}</div>
                {c.url && <a href={c.url} target="_blank" rel="noopener noreferrer"
                  onClick={e=>e.stopPropagation()}
                  style={{ fontSize:11, color:'#2563eb', textDecoration:'none' }}>Open ↗</a>}
              </div>
              <span style={{ fontSize:12, color:'#64748b' }}>{c.provider || '—'}</span>
              <span onClick={e=>e.stopPropagation()}><SourceBadge source={c.source} /></span>
              <span style={{ fontSize:12, color:'#64748b' }}>{c.frequency_days ? `${c.frequency_days}d` : '—'}</span>
              <div onClick={e=>e.stopPropagation()}>
                <TierPills tiers={c.assigned_tiers} />
                {c.assigned_tiers.filter(t => t.target_role !== 'cfi').map((t, i) => (
                  <button key={i} onClick={() => toggleCourseExemptInCat(c.id, t.tier_id, t.target_role, t.scoring_exempt)}
                    title={t.scoring_exempt ? 'Click to count toward score' : 'Click to make info only'}
                    style={{ display:'inline-block', marginTop:3, marginRight:3, padding:'1px 7px',
                      borderRadius:4, fontSize:10, fontWeight:700, cursor:'pointer',
                      background: t.scoring_exempt ? 'rgba(248,113,113,0.1)' : '#f0fdf4',
                      color: t.scoring_exempt ? '#f87171' : '#16a34a',
                      border: `1px solid ${t.scoring_exempt ? '#f8717133' : '#bbf7d0'}` }}>
                    {t.scoring_exempt ? '👁 info only' : '✓ scored'}
                  </button>
                ))}
              </div>
              <div onClick={e=>e.stopPropagation()} style={{ display:'flex', justifyContent:'flex-end', gap:4 }}>
                <button onClick={() => setAssignModal({ ids:[c.id], label:c.title })}
                  style={{ padding:'3px 8px', borderRadius:5, fontSize:10, fontWeight:600, cursor:'pointer',
                    background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe' }}>Assign</button>
                {c.source === 'school' && (
                  <button onClick={() => deleteCourse(c)}
                    style={{ padding:'3px 8px', borderRadius:5, fontSize:10, cursor:'pointer',
                      background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca' }}>Del</button>
                )}
              </div>
            </div>
          ))}
          {!loading && courses.length === 0 && (
            <div style={{ padding:40, textAlign:'center', color:'#94a3b8', fontSize:14 }}>
              No courses found. Add a school course or adjust filters.
            </div>
          )}
        </div>
      </div>

      {/* Side panel */}
      {(selected || adding) && (
        <CourseEditPanel
          course={adding ? null : selected}
          onClose={() => { setSelected(null); setAdding(false); }}
          onSave={async (d) => {
            if (adding) {
              await apiPost('/api/training/school-courses', d, token);
              setAdding(false);
            } else {
              await apiPut(`/api/training/school-courses/${selected.id}`, d, token);
              setSelected(null);
            }
            load();
          }}
          onDelete={selected?.source==='school' ? () => deleteCourse(selected) : null}
        />
      )}

      {assignModal && (
        <AssignToTierModal
          tiers={tiers}
          label={assignModal.label}
          onAssign={async (tierId, targetRole, scoringExempt) => {
            await apiPost('/api/training/courses/assign', { course_ids: assignModal.ids, tier_id: tierId, target_role: targetRole, scoring_exempt: scoringExempt }, token);
            setAssignModal(null); load();
          }}
          onClose={() => setAssignModal(null)}
        />
      )}
    </div>
  );
}

function CourseEditPanel({ course, onClose, onSave, onDelete }) {
  const isNew = !course;
  const canEdit = isNew || course?.source === 'school';
  const [form, setForm] = useState({
    title: course?.title || '',
    description: course?.description || '',
    url: course?.url || '',
    provider: course?.provider || 'School',
    category: course?.category || '',
    frequency_days: course?.frequency_days || 365,
    applies_to: course?.applies_to || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(p => ({...p, [k]:v}));

  const handle = async () => {
    setSaving(true);
    try { await onSave(form); } catch(e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ width:300, background:'#fff', border:'1px solid #e2e8f0', borderRadius:10,
      padding:20, height:'fit-content', position:'sticky', top:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:14, fontWeight:700, color:'#1e293b' }}>
          {isNew ? 'New Course' : canEdit ? 'Edit Course' : 'View Course'}
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:18, cursor:'pointer' }}>✕</button>
      </div>
      {!canEdit && <div style={{ marginBottom:12, padding:'8px 10px', background:'#fffbeb', borderRadius:6,
        fontSize:12, color:'#92400e', border:'1px solid #fde68a' }}>
        Platform course — visibility only editable.
      </div>}
      {[
        { label:'Title', key:'title', disabled:!canEdit },
        { label:'URL', key:'url', disabled:!canEdit },
        { label:'Provider', key:'provider', disabled:!canEdit },
        { label:'Category', key:'category', disabled:!canEdit },
        { label:'Applies To', key:'applies_to', disabled:!canEdit },
        { label:'Frequency (days)', key:'frequency_days', type:'number', disabled:false },
      ].map(f => (
        <div key={f.key} style={{ marginBottom:12 }}>
          <div style={{ fontSize:11, color:'#64748b', marginBottom:4, fontWeight:600 }}>{f.label}</div>
          <input type={f.type||'text'} value={form[f.key]} disabled={f.disabled}
            onChange={e => set(f.key, f.type==='number' ? parseInt(e.target.value)||0 : e.target.value)}
            style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid #e2e8f0',
              fontSize:13, color:'#1e293b', background: f.disabled ? '#f8fafc' : '#fff',
              boxSizing:'border-box' }} />
        </div>
      ))}
      <div style={{ marginBottom:12 }}>
        <div style={{ fontSize:11, color:'#64748b', marginBottom:4, fontWeight:600 }}>Description</div>
        <textarea value={form.description} disabled={!canEdit} rows={3}
          onChange={e => set('description', e.target.value)}
          style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid #e2e8f0',
            fontSize:12, color:'#1e293b', background: !canEdit ? '#f8fafc' : '#fff',
            boxSizing:'border-box', resize:'vertical' }} />
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={handle} disabled={saving} style={{
          flex:1, padding:'8px', borderRadius:8, background:'#2563eb', color:'#fff',
          border:'none', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
        </button>
        {onDelete && (
          <button onClick={onDelete} style={{ padding:'8px 14px', borderRadius:8, background:'#fef2f2',
            color:'#dc2626', border:'1px solid #fecaca', fontSize:13, cursor:'pointer' }}>Del</button>
        )}
      </div>
    </div>
  );
}

// ── Assignments Tab ───────────────────────────────────────────────────────
function AssignmentsTab() {
  const { token } = useAuth();
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTier, setOpenTier] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/training/assignments-summary', token);
      setSummary(data);
      if (data.length > 0 && openTier === null) setOpenTier(data[0].id);
    } catch(e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [token]);

  const unassignStandard = async (tierId, stdId) => {
    await apiPost('/api/training/acs/unassign', { standard_ids:[stdId], tier_id:tierId }, token);
    load();
  };
  const unassignCourse = async (tierId, courseId) => {
    await apiPost('/api/training/courses/unassign', { course_ids:[courseId], tier_id:tierId }, token);
    load();
  };
  const toggleStandardExempt = async (tierId, stdId, targetRole, currentExempt) => {
    await apiPost('/api/training/acs/assign', { standard_ids:[stdId], tier_id:tierId, target_role:targetRole, scoring_exempt:!currentExempt }, token);
    load();
  };
  const toggleCourseExempt = async (tierId, courseId, targetRole, currentExempt) => {
    await apiPost('/api/training/courses/assign', { course_ids:[courseId], tier_id:tierId, target_role:targetRole, scoring_exempt:!currentExempt }, token);
    load();
  };

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>Loading…</div>;
  if (summary.length === 0) return (
    <div style={{ padding:60, textAlign:'center', color:'#94a3b8' }}>
      <div style={{ fontSize:32, marginBottom:12 }}>🎯</div>
      <div style={{ fontSize:15, fontWeight:600, color:'#475569', marginBottom:8 }}>No assignments yet</div>
      <div style={{ fontSize:13 }}>Go to ACS Standards or Course Catalogue and assign items to tiers.</div>
    </div>
  );

  return (
    <div>
      {/* Tier selector */}
      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
        {summary.map(t => (
          <button key={t.id} onClick={() => setOpenTier(t.id)} style={{
            padding:'8px 16px', borderRadius:8, cursor:'pointer', border:'1px solid',
            borderColor: openTier===t.id ? '#2563eb' : '#e2e8f0',
            background: openTier===t.id ? '#eff6ff' : '#fff',
            color: openTier===t.id ? '#2563eb' : '#475569',
            fontWeight: openTier===t.id ? 700 : 400, fontSize:13
          }}>
            {t.name || 'All Tiers'}
            <span style={{ marginLeft:6, fontSize:11, color: openTier===t.id ? '#2563eb' : '#94a3b8' }}>
              {t.pilot_count} pilot{t.pilot_count!==1?'s':''}
            </span>
          </button>
        ))}
      </div>

      {summary.filter(t => t.id === openTier).map(tier => (
        <div key={tier.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          {/* Standards */}
          <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #e2e8f0', background:'#f8fafc',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#1e293b' }}>
                ACS Standards <span style={{ fontSize:12, color:'#94a3b8', fontWeight:400 }}>({tier.standards.length})</span>
              </div>
            </div>
            {tier.standards.length === 0 ? (
              <div style={{ padding:30, textAlign:'center', color:'#94a3b8', fontSize:13 }}>
                No standards assigned. Use ACS Standards tab to assign.
              </div>
            ) : tier.standards.map((s, i) => (
              <div key={s.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 14px',
                borderBottom: i < tier.standards.length-1 ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:500, color:'#1e293b', whiteSpace:'nowrap',
                    overflow:'hidden', textOverflow:'ellipsis' }}>{s.task}</div>
                  <div style={{ display:'flex', gap:6, marginTop:2, flexWrap:'wrap' }}>
                    <CertPill cert={s.cert_level} />
                    <SourceBadge source={s.source} />
                    {s.target_role === 'cfi' && <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4, background:'#ecfdf5', color:'#059669', border:'1px solid #a7f3d0' }}>🎓 CFI</span>}
                    {s.target_role === 'all' && <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4, background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe' }}>All</span>}
                  </div>
                </div>
                <span style={{ fontSize:11, color:'#94a3b8', whiteSpace:'nowrap' }}>{s.frequency_days}d</span>
                {!s.scoring_exempt && s.target_role !== 'cfi' && (
                  <span style={{ padding:'2px 7px', borderRadius:4, fontSize:10, fontWeight:800,
                    background:'rgba(167,139,250,0.12)', color:'#8b5cf6', border:'1px solid #a78bfa44' }}>
                    {s.point_value || 1}pt
                  </span>
                )}
                <button onClick={() => toggleStandardExempt(tier.id, s.id, s.target_role, s.scoring_exempt)}
                  title={s.scoring_exempt ? 'Click to count toward score' : 'Click to make info only'}
                  style={{ padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:700, cursor:'pointer',
                    background: s.scoring_exempt ? 'rgba(248,113,113,0.1)' : '#f0fdf4',
                    color: s.scoring_exempt ? '#f87171' : '#16a34a',
                    border: `1px solid ${s.scoring_exempt ? '#f8717133' : '#bbf7d0'}` }}>
                  {s.scoring_exempt ? 'info only' : '✓ scored'}
                </button>
                <button onClick={() => unassignStandard(tier.id, s.id)}
                  style={{ padding:'2px 8px', borderRadius:4, fontSize:11, cursor:'pointer',
                    background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca' }}>✕</button>
              </div>
            ))}
          </div>

          {/* Courses */}
          <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #e2e8f0', background:'#f8fafc',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#1e293b' }}>
                Courses <span style={{ fontSize:12, color:'#94a3b8', fontWeight:400 }}>({tier.courses.length})</span>
              </div>
            </div>
            {tier.courses.length === 0 ? (
              <div style={{ padding:30, textAlign:'center', color:'#94a3b8', fontSize:13 }}>
                No courses assigned. Use Course Catalogue tab to assign.
              </div>
            ) : tier.courses.map((c, i) => (
              <div key={c.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 14px',
                borderBottom: i < tier.courses.length-1 ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:500, color:'#1e293b', whiteSpace:'nowrap',
                    overflow:'hidden', textOverflow:'ellipsis' }}>{c.title}</div>
                  <div style={{ display:'flex', gap:6, marginTop:2 }}>
                    <span style={{ fontSize:10, color:'#64748b' }}>{c.provider}</span>
                    <SourceBadge source={c.source} />
                    {c.target_role === 'cfi' && <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4, background:'#ecfdf5', color:'#059669', border:'1px solid #a7f3d0' }}>🎓 CFI</span>}
                    {c.target_role === 'all' && <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4, background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe' }}>All</span>}
                  </div>
                </div>
                {c.url && <a href={c.url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize:11, color:'#2563eb', textDecoration:'none' }}>↗</a>}
                <button onClick={() => toggleCourseExempt(tier.id, c.id, c.target_role, c.scoring_exempt)}
                  title={c.scoring_exempt ? 'Click to count toward score' : 'Click to make info only'}
                  style={{ padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:700, cursor:'pointer',
                    background: c.scoring_exempt ? 'rgba(248,113,113,0.1)' : '#f0fdf4',
                    color: c.scoring_exempt ? '#f87171' : '#16a34a',
                    border: `1px solid ${c.scoring_exempt ? '#f8717133' : '#bbf7d0'}` }}>
                  {c.scoring_exempt ? 'info only' : '✓ scored'}
                </button>
                <button onClick={() => unassignCourse(tier.id, c.id)}
                  style={{ padding:'2px 8px', borderRadius:4, fontSize:11, cursor:'pointer',
                    background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca' }}>✕</button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Assign to Tier Modal ──────────────────────────────────────────────────
function AssignToTierModal({ tiers, label, onAssign, onClose, showPoints = false }) {
  const [tierId, setTierId]       = useState(null);
  const [targetRole, setRole]     = useState('pilot');
  const [scoringExempt, setScoringExempt] = useState(true);
  const [pointValue, setPointValue] = useState(1);
  const [saving, setSaving]       = useState(false);

  const handle = async () => {
    setSaving(true);
    try { await onAssign(tierId, targetRole, scoringExempt, pointValue); } catch(e) { alert(e.message); }
    setSaving(false);
  };

  const RoleBtn = ({ val, icon, title, sub }) => (
    <button onClick={() => setRole(val)} style={{
      flex:1, padding:'9px 10px', borderRadius:8, textAlign:'left',
      border:`2px solid ${targetRole===val ? '#2563eb' : '#e2e8f0'}`,
      background: targetRole===val ? '#eff6ff' : '#fff',
      color: targetRole===val ? '#2563eb' : '#475569', cursor:'pointer'
    }}>
      <div style={{ fontSize:13, fontWeight:700 }}>{icon} {title}</div>
      <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{sub}</div>
    </button>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:12, padding:24, width:400,
        boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }} onClick={e=>e.stopPropagation()}>
        <div style={{ fontSize:15, fontWeight:700, color:'#1e293b', marginBottom:4 }}>Assign to Tier / Role</div>
        <div style={{ fontSize:12, color:'#64748b', marginBottom:14, wordBreak:'break-word' }}>"{label}"</div>

        {/* Role selector */}
        <div style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase',
          letterSpacing:'0.07em', marginBottom:6 }}>Who receives this?</div>
        <div style={{ display:'flex', gap:6, marginBottom:16 }}>
          <RoleBtn val="pilot" icon="🧑‍✈️" title="Pilots" sub="Student & renter tiers" />
          <RoleBtn val="cfi"   icon="🎓" title="CFI / Staff" sub="Affiliated instructors" />
          <RoleBtn val="all"   icon="🌐" title="Everyone" sub="All linked users" />
        </div>

        {/* Tier selector (only relevant for pilots) */}
        {targetRole !== 'cfi' && (
          <>
            <div style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase',
              letterSpacing:'0.07em', marginBottom:6 }}>Which tier?</div>
            <div style={{ marginBottom:16 }}>
              <button onClick={() => setTierId(null)} style={{
                display:'block', width:'100%', padding:'9px 14px', borderRadius:8, textAlign:'left',
                border:`2px solid ${tierId===null ? '#2563eb' : '#e2e8f0'}`,
                background: tierId===null ? '#eff6ff' : '#fff',
                color: tierId===null ? '#2563eb' : '#475569',
                fontWeight: tierId===null ? 700 : 400, fontSize:13, cursor:'pointer', marginBottom:6
              }}>🌐 All Tiers</button>
              {tiers.map(t => (
                <button key={t.id} onClick={() => setTierId(t.id)} style={{
                  display:'block', width:'100%', padding:'9px 14px', borderRadius:8, textAlign:'left',
                  border:`2px solid ${tierId===t.id ? '#2563eb' : '#e2e8f0'}`,
                  background: tierId===t.id ? '#eff6ff' : '#fff',
                  color: tierId===t.id ? '#2563eb' : '#475569',
                  fontWeight: tierId===t.id ? 700 : 400, fontSize:13, cursor:'pointer', marginBottom:6
                }}>{t.name}</button>
              ))}
            </div>
          </>
        )}
        {targetRole === 'cfi' ? (
          <div style={{ padding:'10px 12px', borderRadius:8, background:'#f0fdf4', border:'1px solid #bbf7d0',
            fontSize:12, color:'#166534', marginBottom:16 }}>
            Assigned to all CFIs / staff. Appears in their PWA <strong>for awareness only — never counted toward their score</strong>.
          </div>
        ) : (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase',
              letterSpacing:'0.07em', marginBottom:6 }}>Proficiency scoring</div>
            <div style={{ display:'flex', gap:8 }}>
              {[{val:false,label:'✅ Count toward score',sub:'Included in proficiency'},{val:true,label:'👁 Info only',sub:'Visible but not scored'}].map(opt => (
                <button key={String(opt.val)} onClick={() => setScoringExempt(opt.val)} style={{
                  flex:1, padding:'9px 10px', borderRadius:8, textAlign:'left',
                  border:`2px solid ${scoringExempt===opt.val ? '#2563eb' : '#e2e8f0'}`,
                  background: scoringExempt===opt.val ? '#eff6ff' : '#fff',
                  color: scoringExempt===opt.val ? '#2563eb' : '#475569', cursor:'pointer'
                }}>
                  <div style={{ fontSize:12, fontWeight:700 }}>{opt.label}</div>
                  <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{opt.sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {showPoints && targetRole !== 'cfi' && !scoringExempt && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase',
              letterSpacing:'0.07em', marginBottom:6 }}>Point Value</div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              {[1,2,3,5].map(v => (
                <button key={v} onClick={() => setPointValue(v)} style={{
                  width:40, height:40, borderRadius:8, fontWeight:700, fontSize:14, cursor:'pointer',
                  border:`2px solid ${pointValue===v ? '#2563eb' : '#e2e8f0'}`,
                  background: pointValue===v ? '#eff6ff' : '#fff',
                  color: pointValue===v ? '#2563eb' : '#475569'
                }}>{v}</button>
              ))}
              <input type="number" min={1} max={10} value={pointValue}
                onChange={e => setPointValue(Math.max(1,parseInt(e.target.value)||1))}
                style={{ width:56, padding:'8px', borderRadius:8, border:'1px solid #e2e8f0',
                  fontSize:14, fontWeight:700, textAlign:'center', color:'#1e293b' }} />
              <span style={{ fontSize:11, color:'#94a3b8' }}>pts</span>
            </div>
            <div style={{ fontSize:11, color:'#94a3b8', marginTop:6 }}>
              Higher value = more weight in the pilot's ACS score. Default 1.
            </div>
          </div>
        )}
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={handle} disabled={saving} style={{
            flex:1, padding:'9px', borderRadius:8, background:'#2563eb', color:'#fff',
            border:'none', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {saving ? 'Assigning…' : 'Assign'}
          </button>
          <button onClick={onClose} style={{ padding:'9px 16px', borderRadius:8, background:'#f8fafc',
            color:'#64748b', border:'1px solid #e2e8f0', fontSize:13, cursor:'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}


function ACSVerificationsPage() {
  const { token } = useAuth();
  const [data, setData] = useState({ pending: [], verified: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [verifying, setVerifying] = useState(null);

  const load = () => {
    setLoading(true);
    apiFetch('/api/acs/pending', token)
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]);

  const verify = async (completionId) => {
    setVerifying(completionId);
    try {
      await apiPost(`/api/acs/${completionId}/verify`, {}, token);
      load();
    } catch(e) { alert(e.message); }
    setVerifying(null);
  };

  const unverify = async (completionId) => {
    if (!window.confirm('Remove verification?')) return;
    await apiPost(`/api/acs/${completionId}/unverify`, {}, token);
    load();
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const fmtDt = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
  const gradeColor = (g) => ({ A: '#10b981', B: '#3b82f6', C: '#f59e0b', D: '#f87171' }[g] || '#888');

  const rows = tab === 'pending' ? data.pending : data.verified;

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">ACS Verification Log</h1>
        <p className="page-sub">Review and verify ACS standard completions submitted by your pilots and CFIs.</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setTab('pending')}
          style={{ padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14,
            background: tab === 'pending' ? 'var(--accent)' : 'var(--surface-2)', color: tab === 'pending' ? '#fff' : 'var(--text-muted)' }}>
          Pending {data.pending.length > 0 && <span style={{ marginLeft: 6, background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 12 }}>{data.pending.length}</span>}
        </button>
        <button onClick={() => setTab('verified')}
          style={{ padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14,
            background: tab === 'verified' ? 'var(--accent)' : 'var(--surface-2)', color: tab === 'verified' ? '#fff' : 'var(--text-muted)' }}>
          Verified ({data.verified.length})
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
          <div>{tab === 'pending' ? 'No pending verifications.' : 'No verified items yet.'}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map(r => (
            <div key={r.id} style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: '16px 18px' }}>
              {/* Header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{r.task}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                    {r.cert_level} · {r.category}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, marginLeft: 12 }}>
                  {r.verified ? (
                    <>
                      <span style={{ background: '#10b98122', color: '#10b981', borderRadius: 8, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>✓ Verified</span>
                      <button onClick={() => unverify(r.id)}
                        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '3px 10px', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
                        Undo
                      </button>
                    </>
                  ) : (
                    <button onClick={() => verify(r.id)} disabled={verifying === r.id}
                      style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: verifying === r.id ? 0.6 : 1 }}>
                      {verifying === r.id ? 'Verifying…' : '✓ Verify'}
                    </button>
                  )}
                </div>
              </div>

              {/* Pilot + date */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 13, color: 'var(--text-muted)', marginBottom: r.callsign || r.notes || r.verified ? 10 : 0 }}>
                <span>👤 {r.pilot_name || r.pilot_username}</span>
                <span>📅 Practiced {fmt(r.completed_at)}</span>
                {r.verified_by_type === 'cfi' && r.verified_by_name && (
                  <span>🎓 CFI: {r.verified_by_name}</span>
                )}
              </div>

              {/* Linked flight */}
              {r.callsign && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 8, marginBottom: r.notes || r.verified ? 8 : 0 }}>
                  <span style={{ fontSize: 20, fontWeight: 800, color: gradeColor(r.grade) }}>{r.grade}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                      {r.callsign} → {r.arr_airport}{r.runway_id ? ' RWY ' + r.runway_id : ''} · {r.percentage}%
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmt(r.flight_date)} · {r.source === 'FA' ? 'VFR/FlightAware' : 'FAA SWIM'}</div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {r.notes && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: r.verified ? 8 : 0 }}>
                  "{r.notes}"
                </div>
              )}

              {/* Verified footer */}
              {r.verified && r.verified_at && (
                <div style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>
                  ✓ Verified {fmtDt(r.verified_at)}{r.verified_by_name ? ' by ' + r.verified_by_name : ''}{r.verified_by_role ? ' (' + r.verified_by_role + ')' : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default App;
